import * as z from 'zod';

export const ListBlockedPeriodsRequest = z.object({
  operationId: z
    .string()
    .describe(
      'ID of the restaurant operation this blocked period is associated with. (See the Restaurants Operations API for more information.)'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    from: z
      .date()
      .describe(
        'Start time of the desired window. If not specified, the current time is used.'
      )
      .optional()
      .nullable(),
    until: z.date().describe('End time of the desired window.'),
  }),
});
export const ListBlockedPeriodsResponse = z.object({
  blockPeriods: z
    .array(
      z.object({
        from: z
          .date()
          .describe('The start time at which the restaurant can accept orders')
          .optional()
          .nullable(),
        until: z
          .date()
          .describe('The end time at which the restaurant can accept orders')
          .optional()
          .nullable(),
      })
    )
    .max(1000)
    .optional(),
});
export const GetOperationRequest = z.object({
  operationId: z
    .string()
    .describe('ID of the operation to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetOperationResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Operation ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number. Increments by 1 each time the operation is updated.\nTo prevent conflicting changes,\nthe existing `revision` must be specified when updating an operation.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the operation was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the operation was updated.')
      .optional()
      .nullable(),
    name: z.string().describe('Operation name.').optional().nullable(),
    default: z
      .boolean()
      .describe(
        'Whether the operation is the default operation. <br />\nDefault: `false`.'
      )
      .optional()
      .nullable(),
    fulfillmentIds: z.array(z.string()).max(500).optional(),
    onlineOrderingStatus: z
      .enum([
        'UNDEFINED_ONLINE_ORDERING_STATUS',
        'ENABLED',
        'DISABLED',
        'PAUSED_UNTIL',
      ])
      .describe('Online ordering status of the operation. <br />')
      .optional(),
    defaultFulfillmentType: z
      .enum(['PICKUP', 'DELIVERY'])
      .describe('Default fulfillment type of the operation.')
      .optional(),
    orderScheduling: z
      .intersection(
        z.object({
          type: z
            .enum(['ASAP', 'PREORDER'])
            .describe(
              'Scheduling type. <br />\n- When `ASAP`, `asapOptions` is a required field.\n- When `PREORDER`, `preorderOptions` is a required field.'
            )
            .optional(),
        }),
        z.xor([
          z.object({
            asapOptions: z.never().optional(),
            preorderOptions: z.never().optional(),
          }),
          z.object({
            preorderOptions: z.never().optional(),
            asapOptions: z
              .intersection(
                z.object({
                  preparationTime: z
                    .intersection(
                      z.object({
                        type: z
                          .enum(['MAX_TIME', 'TIME_RANGE'])
                          .describe('Preparation time type.')
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          maxTimeOptions: z.never().optional(),
                          timeRangeOptions: z.never().optional(),
                        }),
                        z.object({
                          timeRangeOptions: z.never().optional(),
                          maxTimeOptions: z
                            .object({
                              timeUnit: z
                                .enum(['MINUTES', 'HOURS', 'DAYS'])
                                .describe('Unit of time for the duration.')
                                .optional(),
                              duration: z
                                .number()
                                .int()
                                .describe(
                                  'Duration value. Unit of time specified in `timeUnit`.'
                                )
                                .min(0)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Options for preparation time. Required when `type` is `MAX_TIME`.'
                            ),
                        }),
                        z.object({
                          maxTimeOptions: z.never().optional(),
                          timeRangeOptions: z
                            .object({
                              timeUnit: z
                                .enum(['MINUTES', 'HOURS', 'DAYS'])
                                .describe('Time unit for the time range.')
                                .optional(),
                              minDuration: z
                                .number()
                                .int()
                                .describe(
                                  'Minimum duration value. Unit of time specified in `timeUnit`.'
                                )
                                .min(1)
                                .optional()
                                .nullable(),
                              maxDuration: z
                                .number()
                                .int()
                                .describe(
                                  'Maximum duration value. Unit of time specified in `timeUnit`.'
                                )
                                .min(1)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Options for preparation time. Required when `type` is `TIME_RANGE`.'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'Amount of time needed to prepare the order. <br />\n- When `MAX_TIME`, `maxTimeOptions` is a required field.\n- When `MAX_RANGE`, `timeRangeOptions` is a required field.'
                    )
                    .optional(),
                  asapFutureHandlingType: z
                    .enum([
                      'NO_FUTURE_HANDLING',
                      'BUSINESS_DAYS_AHEAD_HANDLING',
                    ])
                    .describe(
                      'Defines if and how non-immediate orders should be handled. <br />\nWhen this value is `BUSINESS_DAYS_AHEAD_HANDLING`, `businessDaysAheadHandlingOptions` is a required field.'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    businessDaysAheadHandlingOptions: z.never().optional(),
                  }),
                  z.object({
                    businessDaysAheadHandlingOptions: z
                      .object({
                        daysCount: z
                          .number()
                          .int()
                          .describe(
                            'Number of business days ahead for which orders can be scheduled. <br />\nSetting the `daysCount` to 0 means that orders can be scheduled until the end of the current business day.'
                          )
                          .min(0)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Options for future handling. Required when `asapFutureHandlingType` is `BUSINESS_DAYS_AHEAD_HANDLING`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Options for scheduling. Required if `type` is `ASAP`.'
              ),
          }),
          z.object({
            asapOptions: z.never().optional(),
            preorderOptions: z
              .object({
                method: z
                  .intersection(
                    z.object({
                      type: z
                        .enum(['TIME_BOUNDED', 'WEEKLY_SCHEDULE'])
                        .describe(
                          'Type of time frame for how long in advance preorders can be made. <br />\n- When `TIME_BOUNDED`, `timeBoundedOptions` is a required field.\n- When `WEEKLY_SCHEDULE`, `weeklyScheduleOptions` is a required field.'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        timeBoundedOptions: z.never().optional(),
                        weeklyScheduleOptions: z.never().optional(),
                      }),
                      z.object({
                        weeklyScheduleOptions: z.never().optional(),
                        timeBoundedOptions: z
                          .object({
                            minTimeInAdvance: z
                              .object({
                                timeUnit: z
                                  .enum(['MINUTES', 'HOURS', 'DAYS'])
                                  .describe('Unit of time for the duration.')
                                  .optional(),
                                duration: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration value. Unit of time specified in `timeUnit`.'
                                  )
                                  .min(0)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Minimum time required to schedule the order.'
                              )
                              .optional(),
                            maxTimeInAdvance: z
                              .object({
                                timeUnit: z
                                  .enum(['MINUTES', 'HOURS', 'DAYS'])
                                  .describe('Unit of time for the duration.')
                                  .optional(),
                                duration: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration value. Unit of time specified in `timeUnit`.'
                                  )
                                  .min(0)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Maximum time allowed to schedule the order.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Options for the method. Required when `type` is `TIME_BOUNDED`.'
                          ),
                      }),
                      z.object({
                        timeBoundedOptions: z.never().optional(),
                        weeklyScheduleOptions: z
                          .object({
                            cutOffTime: z
                              .object({
                                dayOfWeek: z
                                  .enum([
                                    'MON',
                                    'TUE',
                                    'WED',
                                    'THU',
                                    'FRI',
                                    'SAT',
                                    'SUN',
                                  ])
                                  .describe('Day of the week.')
                                  .optional(),
                                timeOfDay: z
                                  .object({
                                    hours: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                      )
                                      .optional(),
                                    minutes: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Time of the day.')
                                  .optional(),
                              })
                              .describe(
                                'The weekly schedule cutoff time. <br />\nOrders placed before the cutoff time are scheduled for the current week. <br />\nOrders placed after the cutoff time are scheduled for the next week.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Options for the method. Required when `type` is `WEEKLY_SCHEDULE`.'
                          ),
                      }),
                    ])
                  )
                  .optional(),
                fulfillmentTimesDisplay: z
                  .intersection(
                    z.object({
                      type: z
                        .enum(['TIME_WINDOWS'])
                        .describe(
                          'Type of the fulfillment times. <br />\nWhen `TIME_WINDOWS`, `timeWindowsOptions` is a required field.'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({ timeWindowsOptions: z.never().optional() }),
                      z.object({
                        timeWindowsOptions: z
                          .object({
                            timeUnit: z
                              .enum(['MINUTES', 'HOURS', 'DAYS'])
                              .describe('Unit of time for the duration.')
                              .optional(),
                            duration: z
                              .number()
                              .int()
                              .describe(
                                'Duration value. Unit of time specified in `timeUnit`.'
                              )
                              .min(0)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Options for fulfillment time. Required when `fulfillmentTimesType` is `TIME_WINDOWS`.'
                          ),
                      }),
                    ])
                  )
                  .describe(
                    'Configuration of the fulfillment times. <br />\nCurrently, only `TIME_WINDOWS` is supported.'
                  )
                  .optional(),
              })
              .describe(
                'Options for scheduling. Required if `type` is `PREORDER`.'
              ),
          }),
        ])
      )
      .describe('Information about when an order can be placed for.')
      .optional(),
    operationGroupId: z
      .string()
      .describe('ID of the operation group this operation belongs to.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    businessLocationId: z
      .string()
      .describe(
        'ID of the business location ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of this operation.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe('Extended fields.')
      .optional(),
    tags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
          )
          .optional(),
        tags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe(
        'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of operations.'
      )
      .optional(),
  }),
  z.xor([
    z.object({ pausedUntilOptions: z.never().optional() }),
    z.object({
      pausedUntilOptions: z
        .object({
          time: z
            .date()
            .describe(
              'Date and time until which online ordering is paused. <br />\n\nBefore the specified time, behavior is the same as when `onlineOrderingStatus` is `DISABLED`. <br />\n\nAfter the specified time, behavior is the same as when `onlineOrderingStatus` is `ENABLED`. <br />\n\nPassing the time does not trigger any changes to value of any properties.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Options for online ordering status. Required when `onlineOrderingStatus` is `PAUSED_UNTIL`.'
        ),
    }),
  ])
);
export const UpdateOperationRequest = z.object({
  _id: z
    .string()
    .describe('Operation ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  operation: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Operation ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number. Increments by 1 each time the operation is updated.\nTo prevent conflicting changes,\nthe existing `revision` must be specified when updating an operation.'
          ),
        _createdDate: z
          .date()
          .describe('Date and time the operation was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the operation was updated.')
          .optional()
          .nullable(),
        name: z.string().describe('Operation name.').optional().nullable(),
        default: z
          .boolean()
          .describe(
            'Whether the operation is the default operation. <br />\nDefault: `false`.'
          )
          .optional()
          .nullable(),
        fulfillmentIds: z.array(z.string()).max(500).optional(),
        onlineOrderingStatus: z
          .enum([
            'UNDEFINED_ONLINE_ORDERING_STATUS',
            'ENABLED',
            'DISABLED',
            'PAUSED_UNTIL',
          ])
          .optional(),
        defaultFulfillmentType: z.enum(['PICKUP', 'DELIVERY']).optional(),
        orderScheduling: z
          .intersection(
            z.object({ type: z.enum(['ASAP', 'PREORDER']).optional() }),
            z.xor([
              z.object({
                asapOptions: z.never().optional(),
                preorderOptions: z.never().optional(),
              }),
              z.object({
                preorderOptions: z.never().optional(),
                asapOptions: z
                  .intersection(
                    z.object({
                      preparationTime: z
                        .intersection(
                          z.object({
                            type: z
                              .enum(['MAX_TIME', 'TIME_RANGE'])
                              .describe('Preparation time type.')
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              maxTimeOptions: z.never().optional(),
                              timeRangeOptions: z.never().optional(),
                            }),
                            z.object({
                              timeRangeOptions: z.never().optional(),
                              maxTimeOptions: z
                                .object({
                                  timeUnit: z
                                    .enum(['MINUTES', 'HOURS', 'DAYS'])
                                    .describe('Unit of time for the duration.')
                                    .optional(),
                                  duration: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Duration value. Unit of time specified in `timeUnit`.'
                                    )
                                    .min(0)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Options for preparation time. Required when `type` is `MAX_TIME`.'
                                ),
                            }),
                            z.object({
                              maxTimeOptions: z.never().optional(),
                              timeRangeOptions: z
                                .object({
                                  timeUnit: z
                                    .enum(['MINUTES', 'HOURS', 'DAYS'])
                                    .describe('Time unit for the time range.')
                                    .optional(),
                                  minDuration: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Minimum duration value. Unit of time specified in `timeUnit`.'
                                    )
                                    .min(1)
                                    .optional()
                                    .nullable(),
                                  maxDuration: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Maximum duration value. Unit of time specified in `timeUnit`.'
                                    )
                                    .min(1)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Options for preparation time. Required when `type` is `TIME_RANGE`.'
                                ),
                            }),
                          ])
                        )
                        .describe(
                          'Amount of time needed to prepare the order. <br />\n- When `MAX_TIME`, `maxTimeOptions` is a required field.\n- When `MAX_RANGE`, `timeRangeOptions` is a required field.'
                        )
                        .optional(),
                      asapFutureHandlingType: z
                        .enum([
                          'NO_FUTURE_HANDLING',
                          'BUSINESS_DAYS_AHEAD_HANDLING',
                        ])
                        .describe(
                          'Defines if and how non-immediate orders should be handled. <br />\nWhen this value is `BUSINESS_DAYS_AHEAD_HANDLING`, `businessDaysAheadHandlingOptions` is a required field.'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        businessDaysAheadHandlingOptions: z.never().optional(),
                      }),
                      z.object({
                        businessDaysAheadHandlingOptions: z
                          .object({
                            daysCount: z
                              .number()
                              .int()
                              .describe(
                                'Number of business days ahead for which orders can be scheduled. <br />\nSetting the `daysCount` to 0 means that orders can be scheduled until the end of the current business day.'
                              )
                              .min(0)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Options for future handling. Required when `asapFutureHandlingType` is `BUSINESS_DAYS_AHEAD_HANDLING`.'
                          ),
                      }),
                    ])
                  )
                  .describe(
                    'Options for scheduling. Required if `type` is `ASAP`.'
                  ),
              }),
              z.object({
                asapOptions: z.never().optional(),
                preorderOptions: z
                  .object({
                    method: z
                      .intersection(
                        z.object({
                          type: z
                            .enum(['TIME_BOUNDED', 'WEEKLY_SCHEDULE'])
                            .describe(
                              'Type of time frame for how long in advance preorders can be made. <br />\n- When `TIME_BOUNDED`, `timeBoundedOptions` is a required field.\n- When `WEEKLY_SCHEDULE`, `weeklyScheduleOptions` is a required field.'
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            timeBoundedOptions: z.never().optional(),
                            weeklyScheduleOptions: z.never().optional(),
                          }),
                          z.object({
                            weeklyScheduleOptions: z.never().optional(),
                            timeBoundedOptions: z
                              .object({
                                minTimeInAdvance: z
                                  .object({
                                    timeUnit: z
                                      .enum(['MINUTES', 'HOURS', 'DAYS'])
                                      .describe(
                                        'Unit of time for the duration.'
                                      )
                                      .optional(),
                                    duration: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Duration value. Unit of time specified in `timeUnit`.'
                                      )
                                      .min(0)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Minimum time required to schedule the order.'
                                  )
                                  .optional(),
                                maxTimeInAdvance: z
                                  .object({
                                    timeUnit: z
                                      .enum(['MINUTES', 'HOURS', 'DAYS'])
                                      .describe(
                                        'Unit of time for the duration.'
                                      )
                                      .optional(),
                                    duration: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Duration value. Unit of time specified in `timeUnit`.'
                                      )
                                      .min(0)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Maximum time allowed to schedule the order.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Options for the method. Required when `type` is `TIME_BOUNDED`.'
                              ),
                          }),
                          z.object({
                            timeBoundedOptions: z.never().optional(),
                            weeklyScheduleOptions: z
                              .object({
                                cutOffTime: z
                                  .object({
                                    dayOfWeek: z
                                      .enum([
                                        'MON',
                                        'TUE',
                                        'WED',
                                        'THU',
                                        'FRI',
                                        'SAT',
                                        'SUN',
                                      ])
                                      .describe('Day of the week.')
                                      .optional(),
                                    timeOfDay: z
                                      .object({
                                        hours: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                          )
                                          .optional(),
                                        minutes: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Time of the day.')
                                      .optional(),
                                  })
                                  .describe(
                                    'The weekly schedule cutoff time. <br />\nOrders placed before the cutoff time are scheduled for the current week. <br />\nOrders placed after the cutoff time are scheduled for the next week.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Options for the method. Required when `type` is `WEEKLY_SCHEDULE`.'
                              ),
                          }),
                        ])
                      )
                      .optional(),
                    fulfillmentTimesDisplay: z
                      .intersection(
                        z.object({
                          type: z
                            .enum(['TIME_WINDOWS'])
                            .describe(
                              'Type of the fulfillment times. <br />\nWhen `TIME_WINDOWS`, `timeWindowsOptions` is a required field.'
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            timeWindowsOptions: z.never().optional(),
                          }),
                          z.object({
                            timeWindowsOptions: z
                              .object({
                                timeUnit: z
                                  .enum(['MINUTES', 'HOURS', 'DAYS'])
                                  .describe('Unit of time for the duration.')
                                  .optional(),
                                duration: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration value. Unit of time specified in `timeUnit`.'
                                  )
                                  .min(0)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Options for fulfillment time. Required when `fulfillmentTimesType` is `TIME_WINDOWS`.'
                              ),
                          }),
                        ])
                      )
                      .describe(
                        'Configuration of the fulfillment times. <br />\nCurrently, only `TIME_WINDOWS` is supported.'
                      )
                      .optional(),
                  })
                  .describe(
                    'Options for scheduling. Required if `type` is `PREORDER`.'
                  ),
              }),
            ])
          )
          .describe('Information about when an order can be placed for.')
          .optional(),
        operationGroupId: z
          .string()
          .describe('ID of the operation group this operation belongs to.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        businessLocationId: z
          .string()
          .describe(
            'ID of the business location ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of this operation.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of operations.'
          )
          .optional(),
      }),
      z.xor([
        z.object({ pausedUntilOptions: z.never().optional() }),
        z.object({
          pausedUntilOptions: z
            .object({
              time: z
                .date()
                .describe(
                  'Date and time until which online ordering is paused. <br />\n\nBefore the specified time, behavior is the same as when `onlineOrderingStatus` is `DISABLED`. <br />\n\nAfter the specified time, behavior is the same as when `onlineOrderingStatus` is `ENABLED`. <br />\n\nPassing the time does not trigger any changes to value of any properties.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Options for online ordering status. Required when `onlineOrderingStatus` is `PAUSED_UNTIL`.'
            ),
        }),
      ])
    )
    .describe('Operation to update.'),
});
export const UpdateOperationResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Operation ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number. Increments by 1 each time the operation is updated.\nTo prevent conflicting changes,\nthe existing `revision` must be specified when updating an operation.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the operation was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the operation was updated.')
      .optional()
      .nullable(),
    name: z.string().describe('Operation name.').optional().nullable(),
    default: z
      .boolean()
      .describe(
        'Whether the operation is the default operation. <br />\nDefault: `false`.'
      )
      .optional()
      .nullable(),
    fulfillmentIds: z.array(z.string()).max(500).optional(),
    onlineOrderingStatus: z
      .enum([
        'UNDEFINED_ONLINE_ORDERING_STATUS',
        'ENABLED',
        'DISABLED',
        'PAUSED_UNTIL',
      ])
      .describe('Online ordering status of the operation. <br />')
      .optional(),
    defaultFulfillmentType: z
      .enum(['PICKUP', 'DELIVERY'])
      .describe('Default fulfillment type of the operation.')
      .optional(),
    orderScheduling: z
      .intersection(
        z.object({
          type: z
            .enum(['ASAP', 'PREORDER'])
            .describe(
              'Scheduling type. <br />\n- When `ASAP`, `asapOptions` is a required field.\n- When `PREORDER`, `preorderOptions` is a required field.'
            )
            .optional(),
        }),
        z.xor([
          z.object({
            asapOptions: z.never().optional(),
            preorderOptions: z.never().optional(),
          }),
          z.object({
            preorderOptions: z.never().optional(),
            asapOptions: z
              .intersection(
                z.object({
                  preparationTime: z
                    .intersection(
                      z.object({
                        type: z
                          .enum(['MAX_TIME', 'TIME_RANGE'])
                          .describe('Preparation time type.')
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          maxTimeOptions: z.never().optional(),
                          timeRangeOptions: z.never().optional(),
                        }),
                        z.object({
                          timeRangeOptions: z.never().optional(),
                          maxTimeOptions: z
                            .object({
                              timeUnit: z
                                .enum(['MINUTES', 'HOURS', 'DAYS'])
                                .describe('Unit of time for the duration.')
                                .optional(),
                              duration: z
                                .number()
                                .int()
                                .describe(
                                  'Duration value. Unit of time specified in `timeUnit`.'
                                )
                                .min(0)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Options for preparation time. Required when `type` is `MAX_TIME`.'
                            ),
                        }),
                        z.object({
                          maxTimeOptions: z.never().optional(),
                          timeRangeOptions: z
                            .object({
                              timeUnit: z
                                .enum(['MINUTES', 'HOURS', 'DAYS'])
                                .describe('Time unit for the time range.')
                                .optional(),
                              minDuration: z
                                .number()
                                .int()
                                .describe(
                                  'Minimum duration value. Unit of time specified in `timeUnit`.'
                                )
                                .min(1)
                                .optional()
                                .nullable(),
                              maxDuration: z
                                .number()
                                .int()
                                .describe(
                                  'Maximum duration value. Unit of time specified in `timeUnit`.'
                                )
                                .min(1)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Options for preparation time. Required when `type` is `TIME_RANGE`.'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'Amount of time needed to prepare the order. <br />\n- When `MAX_TIME`, `maxTimeOptions` is a required field.\n- When `MAX_RANGE`, `timeRangeOptions` is a required field.'
                    )
                    .optional(),
                  asapFutureHandlingType: z
                    .enum([
                      'NO_FUTURE_HANDLING',
                      'BUSINESS_DAYS_AHEAD_HANDLING',
                    ])
                    .describe(
                      'Defines if and how non-immediate orders should be handled. <br />\nWhen this value is `BUSINESS_DAYS_AHEAD_HANDLING`, `businessDaysAheadHandlingOptions` is a required field.'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    businessDaysAheadHandlingOptions: z.never().optional(),
                  }),
                  z.object({
                    businessDaysAheadHandlingOptions: z
                      .object({
                        daysCount: z
                          .number()
                          .int()
                          .describe(
                            'Number of business days ahead for which orders can be scheduled. <br />\nSetting the `daysCount` to 0 means that orders can be scheduled until the end of the current business day.'
                          )
                          .min(0)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Options for future handling. Required when `asapFutureHandlingType` is `BUSINESS_DAYS_AHEAD_HANDLING`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Options for scheduling. Required if `type` is `ASAP`.'
              ),
          }),
          z.object({
            asapOptions: z.never().optional(),
            preorderOptions: z
              .object({
                method: z
                  .intersection(
                    z.object({
                      type: z
                        .enum(['TIME_BOUNDED', 'WEEKLY_SCHEDULE'])
                        .describe(
                          'Type of time frame for how long in advance preorders can be made. <br />\n- When `TIME_BOUNDED`, `timeBoundedOptions` is a required field.\n- When `WEEKLY_SCHEDULE`, `weeklyScheduleOptions` is a required field.'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        timeBoundedOptions: z.never().optional(),
                        weeklyScheduleOptions: z.never().optional(),
                      }),
                      z.object({
                        weeklyScheduleOptions: z.never().optional(),
                        timeBoundedOptions: z
                          .object({
                            minTimeInAdvance: z
                              .object({
                                timeUnit: z
                                  .enum(['MINUTES', 'HOURS', 'DAYS'])
                                  .describe('Unit of time for the duration.')
                                  .optional(),
                                duration: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration value. Unit of time specified in `timeUnit`.'
                                  )
                                  .min(0)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Minimum time required to schedule the order.'
                              )
                              .optional(),
                            maxTimeInAdvance: z
                              .object({
                                timeUnit: z
                                  .enum(['MINUTES', 'HOURS', 'DAYS'])
                                  .describe('Unit of time for the duration.')
                                  .optional(),
                                duration: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Duration value. Unit of time specified in `timeUnit`.'
                                  )
                                  .min(0)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Maximum time allowed to schedule the order.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Options for the method. Required when `type` is `TIME_BOUNDED`.'
                          ),
                      }),
                      z.object({
                        timeBoundedOptions: z.never().optional(),
                        weeklyScheduleOptions: z
                          .object({
                            cutOffTime: z
                              .object({
                                dayOfWeek: z
                                  .enum([
                                    'MON',
                                    'TUE',
                                    'WED',
                                    'THU',
                                    'FRI',
                                    'SAT',
                                    'SUN',
                                  ])
                                  .describe('Day of the week.')
                                  .optional(),
                                timeOfDay: z
                                  .object({
                                    hours: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                      )
                                      .optional(),
                                    minutes: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Time of the day.')
                                  .optional(),
                              })
                              .describe(
                                'The weekly schedule cutoff time. <br />\nOrders placed before the cutoff time are scheduled for the current week. <br />\nOrders placed after the cutoff time are scheduled for the next week.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Options for the method. Required when `type` is `WEEKLY_SCHEDULE`.'
                          ),
                      }),
                    ])
                  )
                  .optional(),
                fulfillmentTimesDisplay: z
                  .intersection(
                    z.object({
                      type: z
                        .enum(['TIME_WINDOWS'])
                        .describe(
                          'Type of the fulfillment times. <br />\nWhen `TIME_WINDOWS`, `timeWindowsOptions` is a required field.'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({ timeWindowsOptions: z.never().optional() }),
                      z.object({
                        timeWindowsOptions: z
                          .object({
                            timeUnit: z
                              .enum(['MINUTES', 'HOURS', 'DAYS'])
                              .describe('Unit of time for the duration.')
                              .optional(),
                            duration: z
                              .number()
                              .int()
                              .describe(
                                'Duration value. Unit of time specified in `timeUnit`.'
                              )
                              .min(0)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Options for fulfillment time. Required when `fulfillmentTimesType` is `TIME_WINDOWS`.'
                          ),
                      }),
                    ])
                  )
                  .describe(
                    'Configuration of the fulfillment times. <br />\nCurrently, only `TIME_WINDOWS` is supported.'
                  )
                  .optional(),
              })
              .describe(
                'Options for scheduling. Required if `type` is `PREORDER`.'
              ),
          }),
        ])
      )
      .describe('Information about when an order can be placed for.')
      .optional(),
    operationGroupId: z
      .string()
      .describe('ID of the operation group this operation belongs to.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    businessLocationId: z
      .string()
      .describe(
        'ID of the business location ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of this operation.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe('Extended fields.')
      .optional(),
    tags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
          )
          .optional(),
        tags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe(
        'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of operations.'
      )
      .optional(),
  }),
  z.xor([
    z.object({ pausedUntilOptions: z.never().optional() }),
    z.object({
      pausedUntilOptions: z
        .object({
          time: z
            .date()
            .describe(
              'Date and time until which online ordering is paused. <br />\n\nBefore the specified time, behavior is the same as when `onlineOrderingStatus` is `DISABLED`. <br />\n\nAfter the specified time, behavior is the same as when `onlineOrderingStatus` is `ENABLED`. <br />\n\nPassing the time does not trigger any changes to value of any properties.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Options for online ordering status. Required when `onlineOrderingStatus` is `PAUSED_UNTIL`.'
        ),
    }),
  ])
);
export const DeleteOperationRequest = z.object({
  operationId: z
    .string()
    .describe('ID of the operation to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteOperationResponse = z.object({});
export const QueryOperationRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          name: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          default: z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          fulfillmentIds: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          defaultFulfillmentType: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          onlineOrderingStatus: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'businessLocationDetails.archived': z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          businessLocationId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                '_id',
                '_createdDate',
                '_updatedDate',
                'name',
                'default',
                'fulfillmentIds',
                'defaultFulfillmentType',
                'onlineOrderingStatus',
                'businessLocationDetails.archived',
                'businessLocationId',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
});
export const QueryOperationResponse = z.object({
  operations: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Operation ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number. Increments by 1 each time the operation is updated.\nTo prevent conflicting changes,\nthe existing `revision` must be specified when updating an operation.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the operation was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the operation was updated.')
            .optional()
            .nullable(),
          name: z.string().describe('Operation name.').optional().nullable(),
          default: z
            .boolean()
            .describe(
              'Whether the operation is the default operation. <br />\nDefault: `false`.'
            )
            .optional()
            .nullable(),
          fulfillmentIds: z.array(z.string()).max(500).optional(),
          onlineOrderingStatus: z
            .enum([
              'UNDEFINED_ONLINE_ORDERING_STATUS',
              'ENABLED',
              'DISABLED',
              'PAUSED_UNTIL',
            ])
            .describe('Online ordering status of the operation. <br />')
            .optional(),
          defaultFulfillmentType: z
            .enum(['PICKUP', 'DELIVERY'])
            .describe('Default fulfillment type of the operation.')
            .optional(),
          orderScheduling: z
            .intersection(
              z.object({
                type: z
                  .enum(['ASAP', 'PREORDER'])
                  .describe(
                    'Scheduling type. <br />\n- When `ASAP`, `asapOptions` is a required field.\n- When `PREORDER`, `preorderOptions` is a required field.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  asapOptions: z.never().optional(),
                  preorderOptions: z.never().optional(),
                }),
                z.object({
                  preorderOptions: z.never().optional(),
                  asapOptions: z
                    .intersection(
                      z.object({
                        preparationTime: z
                          .intersection(
                            z.object({
                              type: z
                                .enum(['MAX_TIME', 'TIME_RANGE'])
                                .describe('Preparation time type.')
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                maxTimeOptions: z.never().optional(),
                                timeRangeOptions: z.never().optional(),
                              }),
                              z.object({
                                timeRangeOptions: z.never().optional(),
                                maxTimeOptions: z
                                  .object({
                                    timeUnit: z
                                      .enum(['MINUTES', 'HOURS', 'DAYS'])
                                      .describe(
                                        'Unit of time for the duration.'
                                      )
                                      .optional(),
                                    duration: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Duration value. Unit of time specified in `timeUnit`.'
                                      )
                                      .min(0)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Options for preparation time. Required when `type` is `MAX_TIME`.'
                                  ),
                              }),
                              z.object({
                                maxTimeOptions: z.never().optional(),
                                timeRangeOptions: z
                                  .object({
                                    timeUnit: z
                                      .enum(['MINUTES', 'HOURS', 'DAYS'])
                                      .describe('Time unit for the time range.')
                                      .optional(),
                                    minDuration: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Minimum duration value. Unit of time specified in `timeUnit`.'
                                      )
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                    maxDuration: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Maximum duration value. Unit of time specified in `timeUnit`.'
                                      )
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Options for preparation time. Required when `type` is `TIME_RANGE`.'
                                  ),
                              }),
                            ])
                          )
                          .describe(
                            'Amount of time needed to prepare the order. <br />\n- When `MAX_TIME`, `maxTimeOptions` is a required field.\n- When `MAX_RANGE`, `timeRangeOptions` is a required field.'
                          )
                          .optional(),
                        asapFutureHandlingType: z
                          .enum([
                            'NO_FUTURE_HANDLING',
                            'BUSINESS_DAYS_AHEAD_HANDLING',
                          ])
                          .describe(
                            'Defines if and how non-immediate orders should be handled. <br />\nWhen this value is `BUSINESS_DAYS_AHEAD_HANDLING`, `businessDaysAheadHandlingOptions` is a required field.'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          businessDaysAheadHandlingOptions: z
                            .never()
                            .optional(),
                        }),
                        z.object({
                          businessDaysAheadHandlingOptions: z
                            .object({
                              daysCount: z
                                .number()
                                .int()
                                .describe(
                                  'Number of business days ahead for which orders can be scheduled. <br />\nSetting the `daysCount` to 0 means that orders can be scheduled until the end of the current business day.'
                                )
                                .min(0)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Options for future handling. Required when `asapFutureHandlingType` is `BUSINESS_DAYS_AHEAD_HANDLING`.'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'Options for scheduling. Required if `type` is `ASAP`.'
                    ),
                }),
                z.object({
                  asapOptions: z.never().optional(),
                  preorderOptions: z
                    .object({
                      method: z
                        .intersection(
                          z.object({
                            type: z
                              .enum(['TIME_BOUNDED', 'WEEKLY_SCHEDULE'])
                              .describe(
                                'Type of time frame for how long in advance preorders can be made. <br />\n- When `TIME_BOUNDED`, `timeBoundedOptions` is a required field.\n- When `WEEKLY_SCHEDULE`, `weeklyScheduleOptions` is a required field.'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              timeBoundedOptions: z.never().optional(),
                              weeklyScheduleOptions: z.never().optional(),
                            }),
                            z.object({
                              weeklyScheduleOptions: z.never().optional(),
                              timeBoundedOptions: z
                                .object({
                                  minTimeInAdvance: z
                                    .object({
                                      timeUnit: z
                                        .enum(['MINUTES', 'HOURS', 'DAYS'])
                                        .describe(
                                          'Unit of time for the duration.'
                                        )
                                        .optional(),
                                      duration: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Duration value. Unit of time specified in `timeUnit`.'
                                        )
                                        .min(0)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      'Minimum time required to schedule the order.'
                                    )
                                    .optional(),
                                  maxTimeInAdvance: z
                                    .object({
                                      timeUnit: z
                                        .enum(['MINUTES', 'HOURS', 'DAYS'])
                                        .describe(
                                          'Unit of time for the duration.'
                                        )
                                        .optional(),
                                      duration: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Duration value. Unit of time specified in `timeUnit`.'
                                        )
                                        .min(0)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      'Maximum time allowed to schedule the order.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Options for the method. Required when `type` is `TIME_BOUNDED`.'
                                ),
                            }),
                            z.object({
                              timeBoundedOptions: z.never().optional(),
                              weeklyScheduleOptions: z
                                .object({
                                  cutOffTime: z
                                    .object({
                                      dayOfWeek: z
                                        .enum([
                                          'MON',
                                          'TUE',
                                          'WED',
                                          'THU',
                                          'FRI',
                                          'SAT',
                                          'SUN',
                                        ])
                                        .describe('Day of the week.')
                                        .optional(),
                                      timeOfDay: z
                                        .object({
                                          hours: z
                                            .number()
                                            .int()
                                            .describe(
                                              'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                            )
                                            .optional(),
                                          minutes: z
                                            .number()
                                            .int()
                                            .describe(
                                              'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Time of the day.')
                                        .optional(),
                                    })
                                    .describe(
                                      'The weekly schedule cutoff time. <br />\nOrders placed before the cutoff time are scheduled for the current week. <br />\nOrders placed after the cutoff time are scheduled for the next week.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Options for the method. Required when `type` is `WEEKLY_SCHEDULE`.'
                                ),
                            }),
                          ])
                        )
                        .optional(),
                      fulfillmentTimesDisplay: z
                        .intersection(
                          z.object({
                            type: z
                              .enum(['TIME_WINDOWS'])
                              .describe(
                                'Type of the fulfillment times. <br />\nWhen `TIME_WINDOWS`, `timeWindowsOptions` is a required field.'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              timeWindowsOptions: z.never().optional(),
                            }),
                            z.object({
                              timeWindowsOptions: z
                                .object({
                                  timeUnit: z
                                    .enum(['MINUTES', 'HOURS', 'DAYS'])
                                    .describe('Unit of time for the duration.')
                                    .optional(),
                                  duration: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Duration value. Unit of time specified in `timeUnit`.'
                                    )
                                    .min(0)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Options for fulfillment time. Required when `fulfillmentTimesType` is `TIME_WINDOWS`.'
                                ),
                            }),
                          ])
                        )
                        .describe(
                          'Configuration of the fulfillment times. <br />\nCurrently, only `TIME_WINDOWS` is supported.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Options for scheduling. Required if `type` is `PREORDER`.'
                    ),
                }),
              ])
            )
            .describe('Information about when an order can be placed for.')
            .optional(),
          operationGroupId: z
            .string()
            .describe('ID of the operation group this operation belongs to.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          businessLocationId: z
            .string()
            .describe(
              'ID of the business location ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of this operation.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe('Extended fields.')
            .optional(),
          tags: z
            .object({
              privateTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                )
                .optional(),
              tags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                )
                .optional(),
            })
            .describe(
              'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of operations.'
            )
            .optional(),
        }),
        z.xor([
          z.object({ pausedUntilOptions: z.never().optional() }),
          z.object({
            pausedUntilOptions: z
              .object({
                time: z
                  .date()
                  .describe(
                    'Date and time until which online ordering is paused. <br />\n\nBefore the specified time, behavior is the same as when `onlineOrderingStatus` is `DISABLED`. <br />\n\nAfter the specified time, behavior is the same as when `onlineOrderingStatus` is `ENABLED`. <br />\n\nPassing the time does not trigger any changes to value of any properties.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Options for online ordering status. Required when `onlineOrderingStatus` is `PAUSED_UNTIL`.'
              ),
          }),
        ])
      )
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata of the paginated results.')
    .optional(),
});
export const ListOperationsRequest = z.object({});
export const ListOperationsResponse = z.object({
  operations: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Operation ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number. Increments by 1 each time the operation is updated.\nTo prevent conflicting changes,\nthe existing `revision` must be specified when updating an operation.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the operation was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the operation was updated.')
            .optional()
            .nullable(),
          name: z.string().describe('Operation name.').optional().nullable(),
          default: z
            .boolean()
            .describe(
              'Whether the operation is the default operation. <br />\nDefault: `false`.'
            )
            .optional()
            .nullable(),
          fulfillmentIds: z.array(z.string()).max(500).optional(),
          onlineOrderingStatus: z
            .enum([
              'UNDEFINED_ONLINE_ORDERING_STATUS',
              'ENABLED',
              'DISABLED',
              'PAUSED_UNTIL',
            ])
            .describe('Online ordering status of the operation. <br />')
            .optional(),
          defaultFulfillmentType: z
            .enum(['PICKUP', 'DELIVERY'])
            .describe('Default fulfillment type of the operation.')
            .optional(),
          orderScheduling: z
            .intersection(
              z.object({
                type: z
                  .enum(['ASAP', 'PREORDER'])
                  .describe(
                    'Scheduling type. <br />\n- When `ASAP`, `asapOptions` is a required field.\n- When `PREORDER`, `preorderOptions` is a required field.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  asapOptions: z.never().optional(),
                  preorderOptions: z.never().optional(),
                }),
                z.object({
                  preorderOptions: z.never().optional(),
                  asapOptions: z
                    .intersection(
                      z.object({
                        preparationTime: z
                          .intersection(
                            z.object({
                              type: z
                                .enum(['MAX_TIME', 'TIME_RANGE'])
                                .describe('Preparation time type.')
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                maxTimeOptions: z.never().optional(),
                                timeRangeOptions: z.never().optional(),
                              }),
                              z.object({
                                timeRangeOptions: z.never().optional(),
                                maxTimeOptions: z
                                  .object({
                                    timeUnit: z
                                      .enum(['MINUTES', 'HOURS', 'DAYS'])
                                      .describe(
                                        'Unit of time for the duration.'
                                      )
                                      .optional(),
                                    duration: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Duration value. Unit of time specified in `timeUnit`.'
                                      )
                                      .min(0)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Options for preparation time. Required when `type` is `MAX_TIME`.'
                                  ),
                              }),
                              z.object({
                                maxTimeOptions: z.never().optional(),
                                timeRangeOptions: z
                                  .object({
                                    timeUnit: z
                                      .enum(['MINUTES', 'HOURS', 'DAYS'])
                                      .describe('Time unit for the time range.')
                                      .optional(),
                                    minDuration: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Minimum duration value. Unit of time specified in `timeUnit`.'
                                      )
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                    maxDuration: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Maximum duration value. Unit of time specified in `timeUnit`.'
                                      )
                                      .min(1)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Options for preparation time. Required when `type` is `TIME_RANGE`.'
                                  ),
                              }),
                            ])
                          )
                          .describe(
                            'Amount of time needed to prepare the order. <br />\n- When `MAX_TIME`, `maxTimeOptions` is a required field.\n- When `MAX_RANGE`, `timeRangeOptions` is a required field.'
                          )
                          .optional(),
                        asapFutureHandlingType: z
                          .enum([
                            'NO_FUTURE_HANDLING',
                            'BUSINESS_DAYS_AHEAD_HANDLING',
                          ])
                          .describe(
                            'Defines if and how non-immediate orders should be handled. <br />\nWhen this value is `BUSINESS_DAYS_AHEAD_HANDLING`, `businessDaysAheadHandlingOptions` is a required field.'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          businessDaysAheadHandlingOptions: z
                            .never()
                            .optional(),
                        }),
                        z.object({
                          businessDaysAheadHandlingOptions: z
                            .object({
                              daysCount: z
                                .number()
                                .int()
                                .describe(
                                  'Number of business days ahead for which orders can be scheduled. <br />\nSetting the `daysCount` to 0 means that orders can be scheduled until the end of the current business day.'
                                )
                                .min(0)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Options for future handling. Required when `asapFutureHandlingType` is `BUSINESS_DAYS_AHEAD_HANDLING`.'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'Options for scheduling. Required if `type` is `ASAP`.'
                    ),
                }),
                z.object({
                  asapOptions: z.never().optional(),
                  preorderOptions: z
                    .object({
                      method: z
                        .intersection(
                          z.object({
                            type: z
                              .enum(['TIME_BOUNDED', 'WEEKLY_SCHEDULE'])
                              .describe(
                                'Type of time frame for how long in advance preorders can be made. <br />\n- When `TIME_BOUNDED`, `timeBoundedOptions` is a required field.\n- When `WEEKLY_SCHEDULE`, `weeklyScheduleOptions` is a required field.'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              timeBoundedOptions: z.never().optional(),
                              weeklyScheduleOptions: z.never().optional(),
                            }),
                            z.object({
                              weeklyScheduleOptions: z.never().optional(),
                              timeBoundedOptions: z
                                .object({
                                  minTimeInAdvance: z
                                    .object({
                                      timeUnit: z
                                        .enum(['MINUTES', 'HOURS', 'DAYS'])
                                        .describe(
                                          'Unit of time for the duration.'
                                        )
                                        .optional(),
                                      duration: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Duration value. Unit of time specified in `timeUnit`.'
                                        )
                                        .min(0)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      'Minimum time required to schedule the order.'
                                    )
                                    .optional(),
                                  maxTimeInAdvance: z
                                    .object({
                                      timeUnit: z
                                        .enum(['MINUTES', 'HOURS', 'DAYS'])
                                        .describe(
                                          'Unit of time for the duration.'
                                        )
                                        .optional(),
                                      duration: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Duration value. Unit of time specified in `timeUnit`.'
                                        )
                                        .min(0)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      'Maximum time allowed to schedule the order.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Options for the method. Required when `type` is `TIME_BOUNDED`.'
                                ),
                            }),
                            z.object({
                              timeBoundedOptions: z.never().optional(),
                              weeklyScheduleOptions: z
                                .object({
                                  cutOffTime: z
                                    .object({
                                      dayOfWeek: z
                                        .enum([
                                          'MON',
                                          'TUE',
                                          'WED',
                                          'THU',
                                          'FRI',
                                          'SAT',
                                          'SUN',
                                        ])
                                        .describe('Day of the week.')
                                        .optional(),
                                      timeOfDay: z
                                        .object({
                                          hours: z
                                            .number()
                                            .int()
                                            .describe(
                                              'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                            )
                                            .optional(),
                                          minutes: z
                                            .number()
                                            .int()
                                            .describe(
                                              'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                            )
                                            .optional(),
                                        })
                                        .describe('Time of the day.')
                                        .optional(),
                                    })
                                    .describe(
                                      'The weekly schedule cutoff time. <br />\nOrders placed before the cutoff time are scheduled for the current week. <br />\nOrders placed after the cutoff time are scheduled for the next week.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Options for the method. Required when `type` is `WEEKLY_SCHEDULE`.'
                                ),
                            }),
                          ])
                        )
                        .optional(),
                      fulfillmentTimesDisplay: z
                        .intersection(
                          z.object({
                            type: z
                              .enum(['TIME_WINDOWS'])
                              .describe(
                                'Type of the fulfillment times. <br />\nWhen `TIME_WINDOWS`, `timeWindowsOptions` is a required field.'
                              )
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              timeWindowsOptions: z.never().optional(),
                            }),
                            z.object({
                              timeWindowsOptions: z
                                .object({
                                  timeUnit: z
                                    .enum(['MINUTES', 'HOURS', 'DAYS'])
                                    .describe('Unit of time for the duration.')
                                    .optional(),
                                  duration: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Duration value. Unit of time specified in `timeUnit`.'
                                    )
                                    .min(0)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Options for fulfillment time. Required when `fulfillmentTimesType` is `TIME_WINDOWS`.'
                                ),
                            }),
                          ])
                        )
                        .describe(
                          'Configuration of the fulfillment times. <br />\nCurrently, only `TIME_WINDOWS` is supported.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Options for scheduling. Required if `type` is `PREORDER`.'
                    ),
                }),
              ])
            )
            .describe('Information about when an order can be placed for.')
            .optional(),
          operationGroupId: z
            .string()
            .describe('ID of the operation group this operation belongs to.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          businessLocationId: z
            .string()
            .describe(
              'ID of the business location ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of this operation.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe('Extended fields.')
            .optional(),
          tags: z
            .object({
              privateTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                )
                .optional(),
              tags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                )
                .optional(),
            })
            .describe(
              'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of operations.'
            )
            .optional(),
        }),
        z.xor([
          z.object({ pausedUntilOptions: z.never().optional() }),
          z.object({
            pausedUntilOptions: z
              .object({
                time: z
                  .date()
                  .describe(
                    'Date and time until which online ordering is paused. <br />\n\nBefore the specified time, behavior is the same as when `onlineOrderingStatus` is `DISABLED`. <br />\n\nAfter the specified time, behavior is the same as when `onlineOrderingStatus` is `ENABLED`. <br />\n\nPassing the time does not trigger any changes to value of any properties.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Options for online ordering status. Required when `onlineOrderingStatus` is `PAUSED_UNTIL`.'
              ),
          }),
        ])
      )
    )
    .optional(),
});
export const ListAvailableFulfillmentOptionsRequest = z.object({
  operationId: z
    .string()
    .describe(
      'Operation ID. Returned fulfillment options will belong to this operation.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      deliveryAddress: z
        .intersection(
          z.object({
            country: z.string().describe('Country code.').optional().nullable(),
            subdivision: z
              .string()
              .describe(
                'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
              )
              .optional()
              .nullable(),
            city: z.string().describe('City name.').optional().nullable(),
            postalCode: z
              .string()
              .describe('Zip/postal code.')
              .optional()
              .nullable(),
            addressLine2: z
              .string()
              .describe(
                'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
              )
              .optional()
              .nullable(),
            formattedAddress: z
              .string()
              .describe(
                'A string containing the full address of this location.'
              )
              .optional()
              .nullable(),
            hint: z
              .string()
              .describe('Free text to help find the address.')
              .optional()
              .nullable(),
            geocode: z
              .object({
                latitude: z
                  .number()
                  .describe('Address latitude.')
                  .optional()
                  .nullable(),
                longitude: z
                  .number()
                  .describe('Address longitude.')
                  .optional()
                  .nullable(),
              })
              .describe('Coordinates of the physical address.')
              .optional(),
            countryFullname: z
              .string()
              .describe('Country full name.')
              .optional()
              .nullable(),
            subdivisionFullname: z
              .string()
              .describe('Subdivision full name.')
              .optional()
              .nullable(),
            subdivisions: z
              .array(
                z.object({
                  code: z
                    .string()
                    .describe('Short subdivision code.')
                    .optional(),
                  name: z
                    .string()
                    .describe('Subdivision full name.')
                    .optional(),
                })
              )
              .max(6)
              .optional(),
          }),
          z.xor([
            z.object({
              streetAddress: z.never().optional(),
              addressLine: z.never().optional(),
            }),
            z.object({
              addressLine: z.never().optional(),
              streetAddress: z
                .object({
                  number: z.string().describe('Street number.').optional(),
                  name: z.string().describe('Street name.').optional(),
                  apt: z.string().describe('Apartment number.').optional(),
                  formattedAddressLine: z
                    .string()
                    .describe('Optional address line 1')
                    .optional()
                    .nullable(),
                })
                .describe('Street name and number.'),
            }),
            z.object({
              streetAddress: z.never().optional(),
              addressLine: z
                .string()
                .describe(
                  'Main address line, usually street and number as free text.'
                ),
            }),
          ])
        )
        .describe(
          'Delivery address. Optional.\n\nIf provided, the returned delivery fulfillment options will be able to deliver to this address.'
        )
        .optional(),
    })
    .optional(),
});
export const ListAvailableFulfillmentOptionsResponse = z.object({
  pickupConfigured: z
    .boolean()
    .describe(
      'Whether pickup fulfillment method is configured for the requested operation.'
    )
    .optional(),
  deliveryConfigured: z
    .boolean()
    .describe(
      'Whether delivery fulfillment method is configured for the requested operation.'
    )
    .optional(),
  fulfillmentOptions: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Fulfillment method ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          type: z
            .enum(['PICKUP', 'DELIVERY'])
            .describe('Fulfillment option type.')
            .optional(),
          minOrderPrice: z
            .string()
            .describe(
              'Minimum order price to qualify for the fulfillment option.'
            )
            .optional()
            .nullable(),
          fee: z
            .string()
            .describe('Fee for using the fulfillment option.')
            .optional()
            .nullable(),
          availability: z
            .object({
              startTime: z
                .date()
                .describe(
                  "Date and time at which the fulfillment option's availability starts."
                )
                .optional()
                .nullable(),
              endTime: z
                .date()
                .describe(
                  "Date and time at which the fulfillment option's availability ends."
                )
                .optional()
                .nullable(),
              availableTimes: z
                .array(
                  z.object({
                    dayOfWeek: z
                      .enum(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'])
                      .describe('The day of week this availability relates to.')
                      .optional(),
                    timeRanges: z
                      .array(
                        z.object({
                          startTime: z
                            .object({
                              hours: z
                                .number()
                                .int()
                                .describe(
                                  'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                              minutes: z
                                .number()
                                .int()
                                .describe(
                                  'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                            })
                            .describe(
                              'The start time in time of day representation.'
                            )
                            .optional(),
                          endTime: z
                            .object({
                              hours: z
                                .number()
                                .int()
                                .describe(
                                  'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                              minutes: z
                                .number()
                                .int()
                                .describe(
                                  'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                            })
                            .describe(
                              'The end time in time of day representation.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                  })
                )
                .optional(),
              exceptions: z
                .array(
                  z.object({
                    startTime: z
                      .date()
                      .describe('The start time of the availability exception.')
                      .optional()
                      .nullable(),
                    endTime: z
                      .date()
                      .describe('The end time of the availability exception.')
                      .optional()
                      .nullable(),
                    available: z
                      .boolean()
                      .describe(
                        'An indication whether the exception makes the [`start_time`, `end_time`] range available.'
                      )
                      .optional(),
                    reason: z
                      .string()
                      .describe('The reason for the exception.')
                      .optional()
                      .nullable(),
                  })
                )
                .optional(),
              timeZone: z
                .string()
                .describe('Timezone for which the available times are given.')
                .optional()
                .nullable(),
              asapHandlingAvailable: z
                .boolean()
                .describe(
                  "Whether it's possible to submit an order for as soon as possible handling."
                )
                .optional(),
              futureHandlingAvailable: z
                .boolean()
                .describe(
                  "Whether it's possible to submit an order for future handling."
                )
                .optional()
                .nullable(),
            })
            .describe('Availability of the fulfillment option.')
            .optional(),
          fulfillmentTimeType: z
            .enum(['UNDEFINED_FULFILLMENT_TIME', 'MAX_TIME', 'DURATION_RANGE'])
            .describe(
              'Fulfillment time type.\nRelevant only to ASAP operations.'
            )
            .optional(),
          fulfillmentTimesDisplayType: z
            .enum(['UNDEFINED_FULFILLMENT_TIMES_DISPLAY', 'TIME_WINDOWS'])
            .describe(
              'Fulfillment times display type. Relevant to preorder operations.'
            )
            .optional(),
          freeFulfillmentPriceThreshold: z
            .string()
            .describe(
              'Minimum order price for free fulfillment.\nIf order price exceeds this amount, the given `fee` is waived.'
            )
            .optional()
            .nullable(),
          instructions: z
            .string()
            .describe('Instructions for the fulfillment.')
            .max(250)
            .optional()
            .nullable(),
          businessLocationId: z
            .string()
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        }),
        z.intersection(
          z.intersection(
            z.xor([
              z.object({
                maxTimeOptions: z.never().optional(),
                durationRangeOptions: z.never().optional(),
              }),
              z.object({
                durationRangeOptions: z.never().optional(),
                maxTimeOptions: z
                  .number()
                  .int()
                  .describe('Fulfillment time has a maximum time.'),
              }),
              z.object({
                maxTimeOptions: z.never().optional(),
                durationRangeOptions: z
                  .object({
                    minDuration: z
                      .number()
                      .int()
                      .describe('Minimum duration in minutes.')
                      .optional(),
                    maxDuration: z
                      .number()
                      .int()
                      .describe('Maximum duration in minutes.')
                      .optional(),
                  })
                  .describe('Fulfillment time is limited by a range.'),
              }),
            ]),
            z.xor([
              z.object({ timeWindowsOptions: z.never().optional() }),
              z.object({
                timeWindowsOptions: z
                  .object({
                    durationInMinutes: z
                      .number()
                      .int()
                      .describe('Time window duration in minutes.')
                      .optional(),
                  })
                  .describe(
                    'Options for fulfillment time. Required when `type` is `TIME_WINDOWS`.'
                  ),
              }),
            ])
          ),
          z.xor([
            z.object({
              pickupOptions: z.never().optional(),
              deliveryOptions: z.never().optional(),
              dineInOptions: z.never().optional(),
            }),
            z.object({
              deliveryOptions: z.never().optional(),
              dineInOptions: z.never().optional(),
              pickupOptions: z
                .object({
                  address: z
                    .object({
                      city: z.string().optional().nullable(),
                      subdivision: z.string().optional().nullable(),
                      country: z.string().optional().nullable(),
                      postalCode: z.string().optional().nullable(),
                      addressLine1: z.string().optional().nullable(),
                      addressLine2: z.string().optional().nullable(),
                    })
                    .describe(
                      "Pickup address. This is the restaurant's address."
                    )
                    .optional(),
                })
                .describe('Information about pickup fulfillment types.'),
            }),
            z.object({
              pickupOptions: z.never().optional(),
              dineInOptions: z.never().optional(),
              deliveryOptions: z
                .object({
                  deliveryProviderAppId: z
                    .string()
                    .describe('Delivery provider app id.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  courierPickupInstructions: z
                    .string()
                    .describe('Pickup instructions for couriers.')
                    .max(250)
                    .optional()
                    .nullable(),
                  deliveryTimeInMinutes: z
                    .number()
                    .int()
                    .describe(
                      'how much time it takes to deliver the order in minutes.'
                    )
                    .min(0)
                    .optional()
                    .nullable(),
                })
                .describe('Information about delivery fulfillment types.'),
            }),
            z.object({
              pickupOptions: z.never().optional(),
              deliveryOptions: z.never().optional(),
              dineInOptions: z
                .object({
                  address: z
                    .object({
                      city: z.string().optional().nullable(),
                      subdivision: z.string().optional().nullable(),
                      country: z.string().optional().nullable(),
                      postalCode: z.string().optional().nullable(),
                      addressLine1: z.string().optional().nullable(),
                      addressLine2: z.string().optional().nullable(),
                    })
                    .describe(
                      "Dine-in address. This is the restaurant's address."
                    )
                    .optional(),
                })
                .describe('Information about dine-in fulfillment types.'),
            }),
          ])
        )
      )
    )
    .optional(),
  blockedByAvailabilityExceptions: z
    .boolean()
    .describe('Whether availability exceptions block the fulfillment options.')
    .optional()
    .nullable(),
  blockedByPausedOrdering: z
    .boolean()
    .describe(
      'Whether availability exceptions from type = PAUSED_ORDERING block the fulfillment options.'
    )
    .optional()
    .nullable(),
});
export const ListFirstAvailableTimeSlotForFulfillmentTypesRequest = z.object({
  operationId: z
    .string()
    .describe(
      'Operation ID.\nReturned fulfillment options will belong to this operation.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      deliveryAddress: z
        .intersection(
          z.object({
            country: z.string().describe('Country code.').optional().nullable(),
            subdivision: z
              .string()
              .describe(
                'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
              )
              .optional()
              .nullable(),
            city: z.string().describe('City name.').optional().nullable(),
            postalCode: z
              .string()
              .describe('Zip/postal code.')
              .optional()
              .nullable(),
            addressLine2: z
              .string()
              .describe(
                'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
              )
              .optional()
              .nullable(),
            formattedAddress: z
              .string()
              .describe(
                'A string containing the full address of this location.'
              )
              .optional()
              .nullable(),
            hint: z
              .string()
              .describe('Free text to help find the address.')
              .optional()
              .nullable(),
            geocode: z
              .object({
                latitude: z
                  .number()
                  .describe('Address latitude.')
                  .optional()
                  .nullable(),
                longitude: z
                  .number()
                  .describe('Address longitude.')
                  .optional()
                  .nullable(),
              })
              .describe('Coordinates of the physical address.')
              .optional(),
            countryFullname: z
              .string()
              .describe('Country full name.')
              .optional()
              .nullable(),
            subdivisionFullname: z
              .string()
              .describe('Subdivision full name.')
              .optional()
              .nullable(),
            subdivisions: z
              .array(
                z.object({
                  code: z
                    .string()
                    .describe('Short subdivision code.')
                    .optional(),
                  name: z
                    .string()
                    .describe('Subdivision full name.')
                    .optional(),
                })
              )
              .max(6)
              .optional(),
          }),
          z.xor([
            z.object({
              streetAddress: z.never().optional(),
              addressLine: z.never().optional(),
            }),
            z.object({
              addressLine: z.never().optional(),
              streetAddress: z
                .object({
                  number: z.string().describe('Street number.').optional(),
                  name: z.string().describe('Street name.').optional(),
                  apt: z.string().describe('Apartment number.').optional(),
                  formattedAddressLine: z
                    .string()
                    .describe('Optional address line 1')
                    .optional()
                    .nullable(),
                })
                .describe('Street name and number.'),
            }),
            z.object({
              streetAddress: z.never().optional(),
              addressLine: z
                .string()
                .describe(
                  'Main address line, usually street and number as free text.'
                ),
            }),
          ])
        )
        .describe(
          'Delivery address. Optional.\n\nIf provided, the returned delivery fulfillment options will be able to deliver to this address.'
        )
        .optional(),
    })
    .optional(),
});
export const ListFirstAvailableTimeSlotForFulfillmentTypesResponse = z.object({
  timeSlots: z
    .array(
      z.object({
        startTime: z
          .date()
          .describe('Start time and date of the time slot.')
          .optional()
          .nullable(),
        endTime: z
          .date()
          .describe('End time and date of the time slot.')
          .optional()
          .nullable(),
        fulfilmentType: z
          .enum(['PICKUP', 'DELIVERY'])
          .describe('Type of the fulfillment.')
          .optional(),
        startsNow: z
          .boolean()
          .describe('Whether the time slot starts now.')
          .optional(),
        fulfillmentDetails: z
          .array(
            z.intersection(
              z.object({
                fee: z
                  .string()
                  .describe('Fee for using this fulfillment.')
                  .optional()
                  .nullable(),
                minOrderPrice: z
                  .string()
                  .describe(
                    'Minimum order price to qualify for using this fulfillment.'
                  )
                  .optional()
                  .nullable(),
                fulfillmentTimeType: z
                  .enum([
                    'UNDEFINED_FULFILLMENT_TIME',
                    'MAX_TIME',
                    'DURATION_RANGE',
                  ])
                  .describe(
                    'Fulfillment time type. Only be relevant to `ASAP` operations.'
                  )
                  .optional(),
                freeFulfillmentPriceThreshold: z
                  .string()
                  .describe(
                    'Minimum order price for free fulfillment.\nIf order price exceeds this amount, the given `fee` is waived.'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  maxTimeOptions: z.never().optional(),
                  durationRangeOptions: z.never().optional(),
                }),
                z.object({
                  durationRangeOptions: z.never().optional(),
                  maxTimeOptions: z
                    .number()
                    .int()
                    .describe('Fulfillment time has a maximum.'),
                }),
                z.object({
                  maxTimeOptions: z.never().optional(),
                  durationRangeOptions: z
                    .object({
                      minDuration: z
                        .number()
                        .int()
                        .describe('Minimum duration in minutes.')
                        .optional(),
                      maxDuration: z
                        .number()
                        .int()
                        .describe('Maximum duration in minutes.')
                        .optional(),
                    })
                    .describe('Fulfillment time has a minimum and a maximum.'),
                }),
              ])
            )
          )
          .max(500)
          .optional(),
        fulfillmentAddress: z
          .object({
            address: z
              .object({
                city: z.string().optional().nullable(),
                subdivision: z.string().optional().nullable(),
                country: z.string().optional().nullable(),
                postalCode: z.string().optional().nullable(),
                addressLine1: z.string().optional().nullable(),
                addressLine2: z.string().optional().nullable(),
              })
              .describe(
                'Pickup address. This is the address of the restaurant.'
              )
              .optional(),
          })
          .describe('Address of the fulfillment.')
          .optional(),
      })
    )
    .optional(),
});
export const CalculateFirstAvailableTimeSlotPerFulfillmentTypeRequest =
  z.object({
    operationId: z
      .string()
      .describe('Operation ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    options: z
      .object({
        deliveryAddress: z
          .intersection(
            z.object({
              country: z
                .string()
                .describe('Country code.')
                .optional()
                .nullable(),
              subdivision: z
                .string()
                .describe(
                  'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                )
                .optional()
                .nullable(),
              city: z.string().describe('City name.').optional().nullable(),
              postalCode: z
                .string()
                .describe('Zip/postal code.')
                .optional()
                .nullable(),
              addressLine2: z
                .string()
                .describe(
                  'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
                )
                .optional()
                .nullable(),
              formattedAddress: z
                .string()
                .describe(
                  'A string containing the full address of this location.'
                )
                .optional()
                .nullable(),
              hint: z
                .string()
                .describe('Free text to help find the address.')
                .optional()
                .nullable(),
              geocode: z
                .object({
                  latitude: z
                    .number()
                    .describe('Address latitude.')
                    .optional()
                    .nullable(),
                  longitude: z
                    .number()
                    .describe('Address longitude.')
                    .optional()
                    .nullable(),
                })
                .describe('Coordinates of the physical address.')
                .optional(),
              countryFullname: z
                .string()
                .describe('Country full name.')
                .optional()
                .nullable(),
              subdivisionFullname: z
                .string()
                .describe('Subdivision full name.')
                .optional()
                .nullable(),
              subdivisions: z
                .array(
                  z.object({
                    code: z
                      .string()
                      .describe('Short subdivision code.')
                      .optional(),
                    name: z
                      .string()
                      .describe('Subdivision full name.')
                      .optional(),
                  })
                )
                .max(6)
                .optional(),
            }),
            z.xor([
              z.object({
                streetAddress: z.never().optional(),
                addressLine: z.never().optional(),
              }),
              z.object({
                addressLine: z.never().optional(),
                streetAddress: z
                  .object({
                    number: z.string().describe('Street number.').optional(),
                    name: z.string().describe('Street name.').optional(),
                    apt: z.string().describe('Apartment number.').optional(),
                    formattedAddressLine: z
                      .string()
                      .describe('Optional address line 1')
                      .optional()
                      .nullable(),
                  })
                  .describe('Street name and number.'),
              }),
              z.object({
                streetAddress: z.never().optional(),
                addressLine: z
                  .string()
                  .describe(
                    'Main address line, usually street and number as free text.'
                  ),
              }),
            ])
          )
          .describe(
            'Delivery address.\n\nThe response includes a time slot with the delivery fulfillment type only if you specify a delivery address.'
          )
          .optional(),
      })
      .optional(),
  });
export const CalculateFirstAvailableTimeSlotPerFulfillmentTypeResponse =
  z.object({
    timeslotsPerFulfillmentType: z
      .array(
        z.object({
          timeSlot: z
            .object({
              startTime: z
                .date()
                .describe(
                  'Start time and date of the time slot in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format.'
                )
                .optional()
                .nullable(),
              endTime: z
                .date()
                .describe(
                  'End time and date of the time slot in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format.'
                )
                .optional()
                .nullable(),
              orderSchedulingType: z
                .enum(['ASAP', 'PREORDER'])
                .describe('Order scheduling type used by the operation.')
                .optional(),
            })
            .describe('Time Slot details.')
            .optional(),
          fulfillmentInfo: z
            .array(
              z.intersection(
                z.object({
                  fee: z
                    .string()
                    .describe('Fee for using this fulfillment option.')
                    .optional()
                    .nullable(),
                  minOrderPrice: z
                    .string()
                    .describe(
                      'Minimum order price to qualify for using this fulfillment option.'
                    )
                    .optional()
                    .nullable(),
                  freeFulfillmentPriceThreshold: z
                    .string()
                    .describe(
                      'Minimum order price to qualify for free fulfillment.\nIf the order price exceeds this amount, the `fee` is waived.'
                    )
                    .optional()
                    .nullable(),
                  address: z
                    .object({
                      city: z.string().optional().nullable(),
                      subdivision: z.string().optional().nullable(),
                      country: z.string().optional().nullable(),
                      postalCode: z.string().optional().nullable(),
                      addressLine1: z.string().optional().nullable(),
                      addressLine2: z.string().optional().nullable(),
                    })
                    .describe(
                      'Details on the address of the fulfillment.\nFor pickup fulfillment types, this is the address to take the order from.\nFor delivery fulfillment types, this is the address to deliver the order to.'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    maxTime: z.never().optional(),
                    durationRange: z.never().optional(),
                  }),
                  z.object({
                    durationRange: z.never().optional(),
                    maxTime: z
                      .number()
                      .int()
                      .describe('Maximum time to fulfill the order.'),
                  }),
                  z.object({
                    maxTime: z.never().optional(),
                    durationRange: z
                      .object({
                        minDuration: z
                          .number()
                          .int()
                          .describe('Minimum duration in minutes.')
                          .optional(),
                        maxDuration: z
                          .number()
                          .int()
                          .describe('Maximum duration in minutes.')
                          .optional(),
                      })
                      .describe('Time range in which to fulfill the order.'),
                  }),
                ])
              )
            )
            .max(500)
            .optional(),
          fulfilmentType: z
            .enum(['PICKUP', 'DELIVERY'])
            .describe('Fulfillment type.')
            .optional(),
        })
      )
      .max(2)
      .optional(),
  });
export const ListFirstAvailableTimeSlotsForOperationsRequest = z.object({
  operationIds: z.array(z.string()).max(100),
  options: z
    .object({
      deliveryAddress: z
        .intersection(
          z.object({
            country: z.string().describe('Country code.').optional().nullable(),
            subdivision: z
              .string()
              .describe(
                'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
              )
              .optional()
              .nullable(),
            city: z.string().describe('City name.').optional().nullable(),
            postalCode: z
              .string()
              .describe('Zip/postal code.')
              .optional()
              .nullable(),
            addressLine2: z
              .string()
              .describe(
                'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
              )
              .optional()
              .nullable(),
            formattedAddress: z
              .string()
              .describe(
                'A string containing the full address of this location.'
              )
              .optional()
              .nullable(),
            hint: z
              .string()
              .describe('Free text to help find the address.')
              .optional()
              .nullable(),
            geocode: z
              .object({
                latitude: z
                  .number()
                  .describe('Address latitude.')
                  .optional()
                  .nullable(),
                longitude: z
                  .number()
                  .describe('Address longitude.')
                  .optional()
                  .nullable(),
              })
              .describe('Coordinates of the physical address.')
              .optional(),
            countryFullname: z
              .string()
              .describe('Country full name.')
              .optional()
              .nullable(),
            subdivisionFullname: z
              .string()
              .describe('Subdivision full name.')
              .optional()
              .nullable(),
            subdivisions: z
              .array(
                z.object({
                  code: z
                    .string()
                    .describe('Short subdivision code.')
                    .optional(),
                  name: z
                    .string()
                    .describe('Subdivision full name.')
                    .optional(),
                })
              )
              .max(6)
              .optional(),
          }),
          z.xor([
            z.object({
              streetAddress: z.never().optional(),
              addressLine: z.never().optional(),
            }),
            z.object({
              addressLine: z.never().optional(),
              streetAddress: z
                .object({
                  number: z.string().describe('Street number.').optional(),
                  name: z.string().describe('Street name.').optional(),
                  apt: z.string().describe('Apartment number.').optional(),
                  formattedAddressLine: z
                    .string()
                    .describe('Optional address line 1')
                    .optional()
                    .nullable(),
                })
                .describe('Street name and number.'),
            }),
            z.object({
              streetAddress: z.never().optional(),
              addressLine: z
                .string()
                .describe(
                  'Main address line, usually street and number as free text.'
                ),
            }),
          ])
        )
        .describe(
          'Delivery address. Optional.\n\nIf provided, the returned delivery fulfillment options will be able to deliver to this address.'
        )
        .optional(),
    })
    .optional(),
});
export const ListFirstAvailableTimeSlotsForOperationsResponse = z.object({
  timeSlots: z
    .array(
      z.object({
        operationId: z
          .string()
          .describe('Operation ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        timeSlots: z
          .array(
            z.object({
              startTime: z
                .date()
                .describe('Start time and date of the time slot.')
                .optional()
                .nullable(),
              endTime: z
                .date()
                .describe('End time and date of the time slot.')
                .optional()
                .nullable(),
              fulfilmentType: z
                .enum(['PICKUP', 'DELIVERY'])
                .describe('Type of the fulfillment.')
                .optional(),
              startsNow: z
                .boolean()
                .describe('Whether the time slot starts now.')
                .optional(),
              fulfillmentDetails: z
                .array(
                  z.intersection(
                    z.object({
                      fee: z
                        .string()
                        .describe('Fee for using this fulfillment.')
                        .optional()
                        .nullable(),
                      minOrderPrice: z
                        .string()
                        .describe(
                          'Minimum order price to qualify for using this fulfillment.'
                        )
                        .optional()
                        .nullable(),
                      fulfillmentTimeType: z
                        .enum([
                          'UNDEFINED_FULFILLMENT_TIME',
                          'MAX_TIME',
                          'DURATION_RANGE',
                        ])
                        .describe(
                          'Fulfillment time type. Only be relevant to `ASAP` operations.'
                        )
                        .optional(),
                      freeFulfillmentPriceThreshold: z
                        .string()
                        .describe(
                          'Minimum order price for free fulfillment.\nIf order price exceeds this amount, the given `fee` is waived.'
                        )
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({
                        maxTimeOptions: z.never().optional(),
                        durationRangeOptions: z.never().optional(),
                      }),
                      z.object({
                        durationRangeOptions: z.never().optional(),
                        maxTimeOptions: z
                          .number()
                          .int()
                          .describe('Fulfillment time has a maximum.'),
                      }),
                      z.object({
                        maxTimeOptions: z.never().optional(),
                        durationRangeOptions: z
                          .object({
                            minDuration: z
                              .number()
                              .int()
                              .describe('Minimum duration in minutes.')
                              .optional(),
                            maxDuration: z
                              .number()
                              .int()
                              .describe('Maximum duration in minutes.')
                              .optional(),
                          })
                          .describe(
                            'Fulfillment time has a minimum and a maximum.'
                          ),
                      }),
                    ])
                  )
                )
                .max(500)
                .optional(),
              fulfillmentAddress: z
                .object({
                  address: z
                    .object({
                      city: z.string().optional().nullable(),
                      subdivision: z.string().optional().nullable(),
                      country: z.string().optional().nullable(),
                      postalCode: z.string().optional().nullable(),
                      addressLine1: z.string().optional().nullable(),
                      addressLine2: z.string().optional().nullable(),
                    })
                    .describe(
                      'Pickup address. This is the address of the restaurant.'
                    )
                    .optional(),
                })
                .describe('Address of the fulfillment.')
                .optional(),
            })
          )
          .max(100)
          .optional(),
      })
    )
    .max(100)
    .optional(),
});
export const CalculateFirstAvailableTimeSlotsPerOperationRequest = z.object({
  operationIds: z.array(z.string()).max(100),
  options: z
    .object({
      deliveryAddress: z
        .intersection(
          z.object({
            country: z.string().describe('Country code.').optional().nullable(),
            subdivision: z
              .string()
              .describe(
                'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
              )
              .optional()
              .nullable(),
            city: z.string().describe('City name.').optional().nullable(),
            postalCode: z
              .string()
              .describe('Zip/postal code.')
              .optional()
              .nullable(),
            addressLine2: z
              .string()
              .describe(
                'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
              )
              .optional()
              .nullable(),
            formattedAddress: z
              .string()
              .describe(
                'A string containing the full address of this location.'
              )
              .optional()
              .nullable(),
            hint: z
              .string()
              .describe('Free text to help find the address.')
              .optional()
              .nullable(),
            geocode: z
              .object({
                latitude: z
                  .number()
                  .describe('Address latitude.')
                  .optional()
                  .nullable(),
                longitude: z
                  .number()
                  .describe('Address longitude.')
                  .optional()
                  .nullable(),
              })
              .describe('Coordinates of the physical address.')
              .optional(),
            countryFullname: z
              .string()
              .describe('Country full name.')
              .optional()
              .nullable(),
            subdivisionFullname: z
              .string()
              .describe('Subdivision full name.')
              .optional()
              .nullable(),
            subdivisions: z
              .array(
                z.object({
                  code: z
                    .string()
                    .describe('Short subdivision code.')
                    .optional(),
                  name: z
                    .string()
                    .describe('Subdivision full name.')
                    .optional(),
                })
              )
              .max(6)
              .optional(),
          }),
          z.xor([
            z.object({
              streetAddress: z.never().optional(),
              addressLine: z.never().optional(),
            }),
            z.object({
              addressLine: z.never().optional(),
              streetAddress: z
                .object({
                  number: z.string().describe('Street number.').optional(),
                  name: z.string().describe('Street name.').optional(),
                  apt: z.string().describe('Apartment number.').optional(),
                  formattedAddressLine: z
                    .string()
                    .describe('Optional address line 1')
                    .optional()
                    .nullable(),
                })
                .describe('Street name and number.'),
            }),
            z.object({
              streetAddress: z.never().optional(),
              addressLine: z
                .string()
                .describe(
                  'Main address line, usually street and number as free text.'
                ),
            }),
          ])
        )
        .describe(
          'Delivery address.\n\nThe response includes time slots with delivery fulfillment types only if you specify a delivery address.'
        )
        .optional(),
    })
    .optional(),
});
export const CalculateFirstAvailableTimeSlotsPerOperationResponse = z.object({
  timeSlotsPerOperation: z
    .array(
      z.object({
        operationId: z
          .string()
          .describe('Operation ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        timeslotsPerFulfillmentType: z
          .array(
            z.object({
              timeSlot: z
                .object({
                  startTime: z
                    .date()
                    .describe(
                      'Start time and date of the time slot in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format.'
                    )
                    .optional()
                    .nullable(),
                  endTime: z
                    .date()
                    .describe(
                      'End time and date of the time slot in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format.'
                    )
                    .optional()
                    .nullable(),
                  orderSchedulingType: z
                    .enum(['ASAP', 'PREORDER'])
                    .describe('Order scheduling type used by the operation.')
                    .optional(),
                })
                .describe('Time Slot details.')
                .optional(),
              fulfillmentInfo: z
                .array(
                  z.intersection(
                    z.object({
                      fee: z
                        .string()
                        .describe('Fee for using this fulfillment option.')
                        .optional()
                        .nullable(),
                      minOrderPrice: z
                        .string()
                        .describe(
                          'Minimum order price to qualify for using this fulfillment option.'
                        )
                        .optional()
                        .nullable(),
                      freeFulfillmentPriceThreshold: z
                        .string()
                        .describe(
                          'Minimum order price to qualify for free fulfillment.\nIf the order price exceeds this amount, the `fee` is waived.'
                        )
                        .optional()
                        .nullable(),
                      address: z
                        .object({
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine1: z.string().optional().nullable(),
                          addressLine2: z.string().optional().nullable(),
                        })
                        .describe(
                          'Details on the address of the fulfillment.\nFor pickup fulfillment types, this is the address to take the order from.\nFor delivery fulfillment types, this is the address to deliver the order to.'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        maxTime: z.never().optional(),
                        durationRange: z.never().optional(),
                      }),
                      z.object({
                        durationRange: z.never().optional(),
                        maxTime: z
                          .number()
                          .int()
                          .describe('Maximum time to fulfill the order.'),
                      }),
                      z.object({
                        maxTime: z.never().optional(),
                        durationRange: z
                          .object({
                            minDuration: z
                              .number()
                              .int()
                              .describe('Minimum duration in minutes.')
                              .optional(),
                            maxDuration: z
                              .number()
                              .int()
                              .describe('Maximum duration in minutes.')
                              .optional(),
                          })
                          .describe(
                            'Time range in which to fulfill the order.'
                          ),
                      }),
                    ])
                  )
                )
                .max(500)
                .optional(),
              fulfilmentType: z
                .enum(['PICKUP', 'DELIVERY'])
                .describe('Fulfillment type.')
                .optional(),
            })
          )
          .max(2)
          .optional(),
      })
    )
    .max(100)
    .optional(),
});
export const ListFirstAvailableTimeSlotsForMenusRequest = z.object({
  operationId: z
    .string()
    .describe(
      'Operation ID.\nReturned timeslots that are belong to this operation.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      deliveryAddress: z
        .intersection(
          z.object({
            country: z.string().describe('Country code.').optional().nullable(),
            subdivision: z
              .string()
              .describe(
                'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
              )
              .optional()
              .nullable(),
            city: z.string().describe('City name.').optional().nullable(),
            postalCode: z
              .string()
              .describe('Zip/postal code.')
              .optional()
              .nullable(),
            addressLine2: z
              .string()
              .describe(
                'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
              )
              .optional()
              .nullable(),
            formattedAddress: z
              .string()
              .describe(
                'A string containing the full address of this location.'
              )
              .optional()
              .nullable(),
            hint: z
              .string()
              .describe('Free text to help find the address.')
              .optional()
              .nullable(),
            geocode: z
              .object({
                latitude: z
                  .number()
                  .describe('Address latitude.')
                  .optional()
                  .nullable(),
                longitude: z
                  .number()
                  .describe('Address longitude.')
                  .optional()
                  .nullable(),
              })
              .describe('Coordinates of the physical address.')
              .optional(),
            countryFullname: z
              .string()
              .describe('Country full name.')
              .optional()
              .nullable(),
            subdivisionFullname: z
              .string()
              .describe('Subdivision full name.')
              .optional()
              .nullable(),
            subdivisions: z
              .array(
                z.object({
                  code: z
                    .string()
                    .describe('Short subdivision code.')
                    .optional(),
                  name: z
                    .string()
                    .describe('Subdivision full name.')
                    .optional(),
                })
              )
              .max(6)
              .optional(),
          }),
          z.xor([
            z.object({
              streetAddress: z.never().optional(),
              addressLine: z.never().optional(),
            }),
            z.object({
              addressLine: z.never().optional(),
              streetAddress: z
                .object({
                  number: z.string().describe('Street number.').optional(),
                  name: z.string().describe('Street name.').optional(),
                  apt: z.string().describe('Apartment number.').optional(),
                  formattedAddressLine: z
                    .string()
                    .describe('Optional address line 1')
                    .optional()
                    .nullable(),
                })
                .describe('Street name and number.'),
            }),
            z.object({
              streetAddress: z.never().optional(),
              addressLine: z
                .string()
                .describe(
                  'Main address line, usually street and number as free text.'
                ),
            }),
          ])
        )
        .describe(
          'Delivery address. Optional.\n\nIf provided, the returned delivery fulfillment options will be able to deliver to this address.'
        )
        .optional(),
      cursorPaging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Maximum number of items to return in the results.')
            .min(0)
            .max(100)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('Cursor paging')
        .optional(),
    })
    .optional(),
});
export const ListFirstAvailableTimeSlotsForMenusResponse = z.object({
  timeSlotsPerMenu: z
    .array(
      z.object({
        menuId: z
          .string()
          .describe('Menu ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        timeslotsPerFulfillmentType: z
          .array(
            z.object({
              startTime: z
                .date()
                .describe('Start time and date of the time slot.')
                .optional()
                .nullable(),
              endTime: z
                .date()
                .describe('End time and date of the time slot.')
                .optional()
                .nullable(),
              fulfilmentType: z
                .enum(['PICKUP', 'DELIVERY'])
                .describe('Type of the fulfillment.')
                .optional(),
              startsNow: z
                .boolean()
                .describe('Whether the time slot starts now.')
                .optional(),
              fulfillmentDetails: z
                .array(
                  z.intersection(
                    z.object({
                      fee: z
                        .string()
                        .describe('Fee for using this fulfillment.')
                        .optional()
                        .nullable(),
                      minOrderPrice: z
                        .string()
                        .describe(
                          'Minimum order price to qualify for using this fulfillment.'
                        )
                        .optional()
                        .nullable(),
                      fulfillmentTimeType: z
                        .enum([
                          'UNDEFINED_FULFILLMENT_TIME',
                          'MAX_TIME',
                          'DURATION_RANGE',
                        ])
                        .describe(
                          'Fulfillment time type. Only be relevant to `ASAP` operations.'
                        )
                        .optional(),
                      freeFulfillmentPriceThreshold: z
                        .string()
                        .describe(
                          'Minimum order price for free fulfillment.\nIf order price exceeds this amount, the given `fee` is waived.'
                        )
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({
                        maxTimeOptions: z.never().optional(),
                        durationRangeOptions: z.never().optional(),
                      }),
                      z.object({
                        durationRangeOptions: z.never().optional(),
                        maxTimeOptions: z
                          .number()
                          .int()
                          .describe('Fulfillment time has a maximum.'),
                      }),
                      z.object({
                        maxTimeOptions: z.never().optional(),
                        durationRangeOptions: z
                          .object({
                            minDuration: z
                              .number()
                              .int()
                              .describe('Minimum duration in minutes.')
                              .optional(),
                            maxDuration: z
                              .number()
                              .int()
                              .describe('Maximum duration in minutes.')
                              .optional(),
                          })
                          .describe(
                            'Fulfillment time has a minimum and a maximum.'
                          ),
                      }),
                    ])
                  )
                )
                .max(500)
                .optional(),
              fulfillmentAddress: z
                .object({
                  address: z
                    .object({
                      city: z.string().optional().nullable(),
                      subdivision: z.string().optional().nullable(),
                      country: z.string().optional().nullable(),
                      postalCode: z.string().optional().nullable(),
                      addressLine1: z.string().optional().nullable(),
                      addressLine2: z.string().optional().nullable(),
                    })
                    .describe(
                      'Pickup address. This is the address of the restaurant.'
                    )
                    .optional(),
                })
                .describe('Address of the fulfillment.')
                .optional(),
            })
          )
          .max(2)
          .optional(),
      })
    )
    .max(100)
    .optional(),
  cursor: z
    .string()
    .describe('Cursor to next request.')
    .max(16000)
    .optional()
    .nullable(),
});
export const CalculateFirstAvailableTimeSlotsPerMenuRequest = z.object({
  operationId: z
    .string()
    .describe('Operation ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      deliveryAddress: z
        .intersection(
          z.object({
            country: z.string().describe('Country code.').optional().nullable(),
            subdivision: z
              .string()
              .describe(
                'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
              )
              .optional()
              .nullable(),
            city: z.string().describe('City name.').optional().nullable(),
            postalCode: z
              .string()
              .describe('Zip/postal code.')
              .optional()
              .nullable(),
            addressLine2: z
              .string()
              .describe(
                'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
              )
              .optional()
              .nullable(),
            formattedAddress: z
              .string()
              .describe(
                'A string containing the full address of this location.'
              )
              .optional()
              .nullable(),
            hint: z
              .string()
              .describe('Free text to help find the address.')
              .optional()
              .nullable(),
            geocode: z
              .object({
                latitude: z
                  .number()
                  .describe('Address latitude.')
                  .optional()
                  .nullable(),
                longitude: z
                  .number()
                  .describe('Address longitude.')
                  .optional()
                  .nullable(),
              })
              .describe('Coordinates of the physical address.')
              .optional(),
            countryFullname: z
              .string()
              .describe('Country full name.')
              .optional()
              .nullable(),
            subdivisionFullname: z
              .string()
              .describe('Subdivision full name.')
              .optional()
              .nullable(),
            subdivisions: z
              .array(
                z.object({
                  code: z
                    .string()
                    .describe('Short subdivision code.')
                    .optional(),
                  name: z
                    .string()
                    .describe('Subdivision full name.')
                    .optional(),
                })
              )
              .max(6)
              .optional(),
          }),
          z.xor([
            z.object({
              streetAddress: z.never().optional(),
              addressLine: z.never().optional(),
            }),
            z.object({
              addressLine: z.never().optional(),
              streetAddress: z
                .object({
                  number: z.string().describe('Street number.').optional(),
                  name: z.string().describe('Street name.').optional(),
                  apt: z.string().describe('Apartment number.').optional(),
                  formattedAddressLine: z
                    .string()
                    .describe('Optional address line 1')
                    .optional()
                    .nullable(),
                })
                .describe('Street name and number.'),
            }),
            z.object({
              streetAddress: z.never().optional(),
              addressLine: z
                .string()
                .describe(
                  'Main address line, usually street and number as free text.'
                ),
            }),
          ])
        )
        .describe(
          'Delivery address.\n\nThe response includes time slots with delivery fulfillment types only if you specify a delivery address.'
        )
        .optional(),
      cursorPaging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Maximum number of items to return in the results.')
            .min(0)
            .max(100)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('Cursor paging.')
        .optional(),
      onlyOnlineOrderingEnabled: z
        .boolean()
        .describe(
          'Only retrieve time slots for menus with online ordering enabled.'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const CalculateFirstAvailableTimeSlotsPerMenuResponse = z.object({
  timeSlotsPerMenu: z
    .array(
      z.object({
        menuId: z
          .string()
          .describe('Menu ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        timeslotsPerFulfillmentType: z
          .array(
            z.object({
              timeSlot: z
                .object({
                  startTime: z
                    .date()
                    .describe(
                      'Start time and date of the time slot in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format.'
                    )
                    .optional()
                    .nullable(),
                  endTime: z
                    .date()
                    .describe(
                      'End time and date of the time slot in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format.'
                    )
                    .optional()
                    .nullable(),
                  orderSchedulingType: z
                    .enum(['ASAP', 'PREORDER'])
                    .describe('Order scheduling type used by the operation.')
                    .optional(),
                })
                .describe('Time Slot details.')
                .optional(),
              fulfillmentInfo: z
                .array(
                  z.intersection(
                    z.object({
                      fee: z
                        .string()
                        .describe('Fee for using this fulfillment option.')
                        .optional()
                        .nullable(),
                      minOrderPrice: z
                        .string()
                        .describe(
                          'Minimum order price to qualify for using this fulfillment option.'
                        )
                        .optional()
                        .nullable(),
                      freeFulfillmentPriceThreshold: z
                        .string()
                        .describe(
                          'Minimum order price to qualify for free fulfillment.\nIf the order price exceeds this amount, the `fee` is waived.'
                        )
                        .optional()
                        .nullable(),
                      address: z
                        .object({
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine1: z.string().optional().nullable(),
                          addressLine2: z.string().optional().nullable(),
                        })
                        .describe(
                          'Details on the address of the fulfillment.\nFor pickup fulfillment types, this is the address to take the order from.\nFor delivery fulfillment types, this is the address to deliver the order to.'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        maxTime: z.never().optional(),
                        durationRange: z.never().optional(),
                      }),
                      z.object({
                        durationRange: z.never().optional(),
                        maxTime: z
                          .number()
                          .int()
                          .describe('Maximum time to fulfill the order.'),
                      }),
                      z.object({
                        maxTime: z.never().optional(),
                        durationRange: z
                          .object({
                            minDuration: z
                              .number()
                              .int()
                              .describe('Minimum duration in minutes.')
                              .optional(),
                            maxDuration: z
                              .number()
                              .int()
                              .describe('Maximum duration in minutes.')
                              .optional(),
                          })
                          .describe(
                            'Time range in which to fulfill the order.'
                          ),
                      }),
                    ])
                  )
                )
                .max(500)
                .optional(),
              fulfilmentType: z
                .enum(['PICKUP', 'DELIVERY'])
                .describe('Fulfillment type.')
                .optional(),
            })
          )
          .max(2)
          .optional(),
      })
    )
    .max(100)
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe('Total number of items that match the query.')
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
  cursorPagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
});
export const CalculateFirstAvailableTimeSlotsForItemRequest = z.object({
  operationId: z
    .string()
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  menuId: z
    .object({ value: z.string().optional() })
    .describe('Menu ID to get the first available time slots for.'),
  options: z.object({
    sectionId: z
      .string()
      .describe('Section ID to get the first available time slots for.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    itemId: z
      .string()
      .describe('Item ID to get the first available time slots for.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    deliveryAddress: z
      .intersection(
        z.object({
          country: z.string().describe('Country code.').optional().nullable(),
          subdivision: z
            .string()
            .describe(
              'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
            )
            .optional()
            .nullable(),
          city: z.string().describe('City name.').optional().nullable(),
          postalCode: z
            .string()
            .describe('Zip/postal code.')
            .optional()
            .nullable(),
          addressLine2: z
            .string()
            .describe(
              'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
            )
            .optional()
            .nullable(),
          formattedAddress: z
            .string()
            .describe('A string containing the full address of this location.')
            .optional()
            .nullable(),
          hint: z
            .string()
            .describe('Free text to help find the address.')
            .optional()
            .nullable(),
          geocode: z
            .object({
              latitude: z
                .number()
                .describe('Address latitude.')
                .optional()
                .nullable(),
              longitude: z
                .number()
                .describe('Address longitude.')
                .optional()
                .nullable(),
            })
            .describe('Coordinates of the physical address.')
            .optional(),
          countryFullname: z
            .string()
            .describe('Country full name.')
            .optional()
            .nullable(),
          subdivisionFullname: z
            .string()
            .describe('Subdivision full name.')
            .optional()
            .nullable(),
          subdivisions: z
            .array(
              z.object({
                code: z.string().describe('Short subdivision code.').optional(),
                name: z.string().describe('Subdivision full name.').optional(),
              })
            )
            .max(6)
            .optional(),
        }),
        z.xor([
          z.object({
            streetAddress: z.never().optional(),
            addressLine: z.never().optional(),
          }),
          z.object({
            addressLine: z.never().optional(),
            streetAddress: z
              .object({
                number: z.string().describe('Street number.').optional(),
                name: z.string().describe('Street name.').optional(),
                apt: z.string().describe('Apartment number.').optional(),
                formattedAddressLine: z
                  .string()
                  .describe('Optional address line 1')
                  .optional()
                  .nullable(),
              })
              .describe('Street name and number.'),
          }),
          z.object({
            streetAddress: z.never().optional(),
            addressLine: z
              .string()
              .describe(
                'Main address line, usually street and number as free text.'
              ),
          }),
        ])
      )
      .describe(
        'Delivery address. Optional.\n\nIf provided, the returned delivery fulfillment options will be able to deliver to this address.'
      )
      .optional(),
  }),
});
export const CalculateFirstAvailableTimeSlotsForItemResponse = z.object({
  timeslotsPerFulfillmentType: z
    .array(
      z.object({
        timeSlot: z
          .object({
            startTime: z
              .date()
              .describe(
                'Start time and date of the time slot in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format.'
              )
              .optional()
              .nullable(),
            endTime: z
              .date()
              .describe(
                'End time and date of the time slot in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format.'
              )
              .optional()
              .nullable(),
            orderSchedulingType: z
              .enum(['ASAP', 'PREORDER'])
              .describe('Order scheduling type used by the operation.')
              .optional(),
          })
          .describe('Time Slot details.')
          .optional(),
        fulfillmentInfo: z
          .array(
            z.intersection(
              z.object({
                fee: z
                  .string()
                  .describe('Fee for using this fulfillment option.')
                  .optional()
                  .nullable(),
                minOrderPrice: z
                  .string()
                  .describe(
                    'Minimum order price to qualify for using this fulfillment option.'
                  )
                  .optional()
                  .nullable(),
                freeFulfillmentPriceThreshold: z
                  .string()
                  .describe(
                    'Minimum order price to qualify for free fulfillment.\nIf the order price exceeds this amount, the `fee` is waived.'
                  )
                  .optional()
                  .nullable(),
                address: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                    addressLine2: z.string().optional().nullable(),
                  })
                  .describe(
                    'Details on the address of the fulfillment.\nFor pickup fulfillment types, this is the address to take the order from.\nFor delivery fulfillment types, this is the address to deliver the order to.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  maxTime: z.never().optional(),
                  durationRange: z.never().optional(),
                }),
                z.object({
                  durationRange: z.never().optional(),
                  maxTime: z
                    .number()
                    .int()
                    .describe('Maximum time to fulfill the order.'),
                }),
                z.object({
                  maxTime: z.never().optional(),
                  durationRange: z
                    .object({
                      minDuration: z
                        .number()
                        .int()
                        .describe('Minimum duration in minutes.')
                        .optional(),
                      maxDuration: z
                        .number()
                        .int()
                        .describe('Maximum duration in minutes.')
                        .optional(),
                    })
                    .describe('Time range in which to fulfill the order.'),
                }),
              ])
            )
          )
          .max(500)
          .optional(),
        fulfilmentType: z
          .enum(['PICKUP', 'DELIVERY'])
          .describe('Fulfillment type.')
          .optional(),
      })
    )
    .optional(),
  cursorPagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
});
export const ListAvailableTimeSlotsForDateRequest = z.object({
  operationId: z
    .string()
    .describe(
      'Operation ID.\nThe returned fulfillment options will belong to this operation.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    deliveryAddress: z
      .intersection(
        z.object({
          country: z.string().describe('Country code.').optional().nullable(),
          subdivision: z
            .string()
            .describe(
              'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
            )
            .optional()
            .nullable(),
          city: z.string().describe('City name.').optional().nullable(),
          postalCode: z
            .string()
            .describe('Zip/postal code.')
            .optional()
            .nullable(),
          addressLine2: z
            .string()
            .describe(
              'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
            )
            .optional()
            .nullable(),
          formattedAddress: z
            .string()
            .describe('A string containing the full address of this location.')
            .optional()
            .nullable(),
          hint: z
            .string()
            .describe('Free text to help find the address.')
            .optional()
            .nullable(),
          geocode: z
            .object({
              latitude: z
                .number()
                .describe('Address latitude.')
                .optional()
                .nullable(),
              longitude: z
                .number()
                .describe('Address longitude.')
                .optional()
                .nullable(),
            })
            .describe('Coordinates of the physical address.')
            .optional(),
          countryFullname: z
            .string()
            .describe('Country full name.')
            .optional()
            .nullable(),
          subdivisionFullname: z
            .string()
            .describe('Subdivision full name.')
            .optional()
            .nullable(),
          subdivisions: z
            .array(
              z.object({
                code: z.string().describe('Short subdivision code.').optional(),
                name: z.string().describe('Subdivision full name.').optional(),
              })
            )
            .max(6)
            .optional(),
        }),
        z.xor([
          z.object({
            streetAddress: z.never().optional(),
            addressLine: z.never().optional(),
          }),
          z.object({
            addressLine: z.never().optional(),
            streetAddress: z
              .object({
                number: z.string().describe('Street number.').optional(),
                name: z.string().describe('Street name.').optional(),
                apt: z.string().describe('Apartment number.').optional(),
                formattedAddressLine: z
                  .string()
                  .describe('Optional address line 1')
                  .optional()
                  .nullable(),
              })
              .describe('Street name and number.'),
          }),
          z.object({
            streetAddress: z.never().optional(),
            addressLine: z
              .string()
              .describe(
                'Main address line, usually street and number as free text.'
              ),
          }),
        ])
      )
      .describe(
        'Delivery address. Optional.\n\nIf provided, the returned delivery fulfillment options will be able to deliver to this address.'
      )
      .optional(),
    date: z
      .object({
        day: z.number().int().describe('The day of the month.').min(1).max(31),
        month: z
          .number()
          .int()
          .describe('The month of the year.')
          .min(1)
          .max(12),
        year: z
          .number()
          .int()
          .describe('The year of the date.')
          .min(2023)
          .max(2200),
      })
      .describe('Date and time to get the available time slots for.'),
  }),
});
export const ListAvailableTimeSlotsForDateResponse = z.object({
  timeSlots: z
    .array(
      z.object({
        startTime: z
          .date()
          .describe('Start time and date of the time slot.')
          .optional()
          .nullable(),
        endTime: z
          .date()
          .describe('End time and date of the time slot.')
          .optional()
          .nullable(),
        fulfilmentType: z
          .enum(['PICKUP', 'DELIVERY'])
          .describe('Type of the fulfillment.')
          .optional(),
        startsNow: z
          .boolean()
          .describe('Whether the time slot starts now.')
          .optional(),
        fulfillmentDetails: z
          .array(
            z.intersection(
              z.object({
                fee: z
                  .string()
                  .describe('Fee for using this fulfillment.')
                  .optional()
                  .nullable(),
                minOrderPrice: z
                  .string()
                  .describe(
                    'Minimum order price to qualify for using this fulfillment.'
                  )
                  .optional()
                  .nullable(),
                fulfillmentTimeType: z
                  .enum([
                    'UNDEFINED_FULFILLMENT_TIME',
                    'MAX_TIME',
                    'DURATION_RANGE',
                  ])
                  .describe(
                    'Fulfillment time type. Only be relevant to `ASAP` operations.'
                  )
                  .optional(),
                freeFulfillmentPriceThreshold: z
                  .string()
                  .describe(
                    'Minimum order price for free fulfillment.\nIf order price exceeds this amount, the given `fee` is waived.'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  maxTimeOptions: z.never().optional(),
                  durationRangeOptions: z.never().optional(),
                }),
                z.object({
                  durationRangeOptions: z.never().optional(),
                  maxTimeOptions: z
                    .number()
                    .int()
                    .describe('Fulfillment time has a maximum.'),
                }),
                z.object({
                  maxTimeOptions: z.never().optional(),
                  durationRangeOptions: z
                    .object({
                      minDuration: z
                        .number()
                        .int()
                        .describe('Minimum duration in minutes.')
                        .optional(),
                      maxDuration: z
                        .number()
                        .int()
                        .describe('Maximum duration in minutes.')
                        .optional(),
                    })
                    .describe('Fulfillment time has a minimum and a maximum.'),
                }),
              ])
            )
          )
          .max(500)
          .optional(),
        fulfillmentAddress: z
          .object({
            address: z
              .object({
                city: z.string().optional().nullable(),
                subdivision: z.string().optional().nullable(),
                country: z.string().optional().nullable(),
                postalCode: z.string().optional().nullable(),
                addressLine1: z.string().optional().nullable(),
                addressLine2: z.string().optional().nullable(),
              })
              .describe(
                'Pickup address. This is the address of the restaurant.'
              )
              .optional(),
          })
          .describe('Address of the fulfillment.')
          .optional(),
      })
    )
    .optional(),
});
export const CalculateAvailableTimeSlotsForDateRequest = z.object({
  operationId: z
    .string()
    .describe('Operation ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    deliveryAddress: z
      .intersection(
        z.object({
          country: z.string().describe('Country code.').optional().nullable(),
          subdivision: z
            .string()
            .describe(
              'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
            )
            .optional()
            .nullable(),
          city: z.string().describe('City name.').optional().nullable(),
          postalCode: z
            .string()
            .describe('Zip/postal code.')
            .optional()
            .nullable(),
          addressLine2: z
            .string()
            .describe(
              'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
            )
            .optional()
            .nullable(),
          formattedAddress: z
            .string()
            .describe('A string containing the full address of this location.')
            .optional()
            .nullable(),
          hint: z
            .string()
            .describe('Free text to help find the address.')
            .optional()
            .nullable(),
          geocode: z
            .object({
              latitude: z
                .number()
                .describe('Address latitude.')
                .optional()
                .nullable(),
              longitude: z
                .number()
                .describe('Address longitude.')
                .optional()
                .nullable(),
            })
            .describe('Coordinates of the physical address.')
            .optional(),
          countryFullname: z
            .string()
            .describe('Country full name.')
            .optional()
            .nullable(),
          subdivisionFullname: z
            .string()
            .describe('Subdivision full name.')
            .optional()
            .nullable(),
          subdivisions: z
            .array(
              z.object({
                code: z.string().describe('Short subdivision code.').optional(),
                name: z.string().describe('Subdivision full name.').optional(),
              })
            )
            .max(6)
            .optional(),
        }),
        z.xor([
          z.object({
            streetAddress: z.never().optional(),
            addressLine: z.never().optional(),
          }),
          z.object({
            addressLine: z.never().optional(),
            streetAddress: z
              .object({
                number: z.string().describe('Street number.').optional(),
                name: z.string().describe('Street name.').optional(),
                apt: z.string().describe('Apartment number.').optional(),
                formattedAddressLine: z
                  .string()
                  .describe('Optional address line 1')
                  .optional()
                  .nullable(),
              })
              .describe('Street name and number.'),
          }),
          z.object({
            streetAddress: z.never().optional(),
            addressLine: z
              .string()
              .describe(
                'Main address line, usually street and number as free text.'
              ),
          }),
        ])
      )
      .describe(
        'Delivery address.\n\nThe response includes time slots with delivery fulfillment types only if you specify a delivery address.'
      )
      .optional(),
    date: z
      .object({
        day: z.number().int().describe('The day of the month.').min(1).max(31),
        month: z
          .number()
          .int()
          .describe('The month of the year.')
          .min(1)
          .max(12),
        year: z
          .number()
          .int()
          .describe('The year of the date.')
          .min(2023)
          .max(2200),
      })
      .describe('Date and time to get the available time slots for.'),
  }),
});
export const CalculateAvailableTimeSlotsForDateResponse = z.object({
  timeslotsPerFulfillmentType: z
    .array(
      z.object({
        timeSlot: z
          .object({
            startTime: z
              .date()
              .describe(
                'Start time and date of the time slot in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format.'
              )
              .optional()
              .nullable(),
            endTime: z
              .date()
              .describe(
                'End time and date of the time slot in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format.'
              )
              .optional()
              .nullable(),
            orderSchedulingType: z
              .enum(['ASAP', 'PREORDER'])
              .describe('Order scheduling type used by the operation.')
              .optional(),
          })
          .describe('Time Slot details.')
          .optional(),
        fulfillmentInfo: z
          .array(
            z.intersection(
              z.object({
                fee: z
                  .string()
                  .describe('Fee for using this fulfillment option.')
                  .optional()
                  .nullable(),
                minOrderPrice: z
                  .string()
                  .describe(
                    'Minimum order price to qualify for using this fulfillment option.'
                  )
                  .optional()
                  .nullable(),
                freeFulfillmentPriceThreshold: z
                  .string()
                  .describe(
                    'Minimum order price to qualify for free fulfillment.\nIf the order price exceeds this amount, the `fee` is waived.'
                  )
                  .optional()
                  .nullable(),
                address: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                    addressLine2: z.string().optional().nullable(),
                  })
                  .describe(
                    'Details on the address of the fulfillment.\nFor pickup fulfillment types, this is the address to take the order from.\nFor delivery fulfillment types, this is the address to deliver the order to.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  maxTime: z.never().optional(),
                  durationRange: z.never().optional(),
                }),
                z.object({
                  durationRange: z.never().optional(),
                  maxTime: z
                    .number()
                    .int()
                    .describe('Maximum time to fulfill the order.'),
                }),
                z.object({
                  maxTime: z.never().optional(),
                  durationRange: z
                    .object({
                      minDuration: z
                        .number()
                        .int()
                        .describe('Minimum duration in minutes.')
                        .optional(),
                      maxDuration: z
                        .number()
                        .int()
                        .describe('Maximum duration in minutes.')
                        .optional(),
                    })
                    .describe('Time range in which to fulfill the order.'),
                }),
              ])
            )
          )
          .max(500)
          .optional(),
        fulfilmentType: z
          .enum(['PICKUP', 'DELIVERY'])
          .describe('Fulfillment type.')
          .optional(),
      })
    )
    .max(2)
    .optional(),
});
export const ListAvailableDatesInRangeRequest = z.object({
  operationId: z
    .string()
    .describe(
      'Operation ID.\nThe returned fulfillment options will belong to this operation.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    deliveryAddress: z
      .intersection(
        z.object({
          country: z.string().describe('Country code.').optional().nullable(),
          subdivision: z
            .string()
            .describe(
              'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
            )
            .optional()
            .nullable(),
          city: z.string().describe('City name.').optional().nullable(),
          postalCode: z
            .string()
            .describe('Zip/postal code.')
            .optional()
            .nullable(),
          addressLine2: z
            .string()
            .describe(
              'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
            )
            .optional()
            .nullable(),
          formattedAddress: z
            .string()
            .describe('A string containing the full address of this location.')
            .optional()
            .nullable(),
          hint: z
            .string()
            .describe('Free text to help find the address.')
            .optional()
            .nullable(),
          geocode: z
            .object({
              latitude: z
                .number()
                .describe('Address latitude.')
                .optional()
                .nullable(),
              longitude: z
                .number()
                .describe('Address longitude.')
                .optional()
                .nullable(),
            })
            .describe('Coordinates of the physical address.')
            .optional(),
          countryFullname: z
            .string()
            .describe('Country full name.')
            .optional()
            .nullable(),
          subdivisionFullname: z
            .string()
            .describe('Subdivision full name.')
            .optional()
            .nullable(),
          subdivisions: z
            .array(
              z.object({
                code: z.string().describe('Short subdivision code.').optional(),
                name: z.string().describe('Subdivision full name.').optional(),
              })
            )
            .max(6)
            .optional(),
        }),
        z.xor([
          z.object({
            streetAddress: z.never().optional(),
            addressLine: z.never().optional(),
          }),
          z.object({
            addressLine: z.never().optional(),
            streetAddress: z
              .object({
                number: z.string().describe('Street number.').optional(),
                name: z.string().describe('Street name.').optional(),
                apt: z.string().describe('Apartment number.').optional(),
                formattedAddressLine: z
                  .string()
                  .describe('Optional address line 1')
                  .optional()
                  .nullable(),
              })
              .describe('Street name and number.'),
          }),
          z.object({
            streetAddress: z.never().optional(),
            addressLine: z
              .string()
              .describe(
                'Main address line, usually street and number as free text.'
              ),
          }),
        ])
      )
      .describe(
        'Delivery address. Optional.\n\nIf provided, the returned delivery fulfillment options will be able to deliver to this address.'
      )
      .optional(),
    from: z
      .object({
        day: z
          .number()
          .int()
          .describe('The day of the month.')
          .min(1)
          .max(31)
          .optional(),
        month: z
          .number()
          .int()
          .describe('The month of the year.')
          .min(1)
          .max(12)
          .optional(),
        year: z
          .number()
          .int()
          .describe('The year of the date.')
          .min(2023)
          .max(2200)
          .optional(),
      })
      .describe('Start date and time of the range.'),
    until: z
      .object({
        day: z
          .number()
          .int()
          .describe('The day of the month.')
          .min(1)
          .max(31)
          .optional(),
        month: z
          .number()
          .int()
          .describe('The month of the year.')
          .min(1)
          .max(12)
          .optional(),
        year: z
          .number()
          .int()
          .describe('The year of the date.')
          .min(2023)
          .max(2200)
          .optional(),
      })
      .describe('End date and time of the range.'),
  }),
});
export const ListAvailableDatesInRangeResponse = z.object({
  availableDates: z
    .array(
      z.object({
        fulfilmentType: z
          .enum(['PICKUP', 'DELIVERY'])
          .describe('Fulfillment type.')
          .optional(),
        dates: z
          .array(
            z.object({
              day: z
                .number()
                .int()
                .describe('The day of the month.')
                .min(1)
                .max(31)
                .optional(),
              month: z
                .number()
                .int()
                .describe('The month of the year.')
                .min(1)
                .max(12)
                .optional(),
              year: z
                .number()
                .int()
                .describe('The year of the date.')
                .min(2023)
                .max(2200)
                .optional(),
            })
          )
          .min(1)
          .max(100)
          .optional(),
      })
    )
    .optional(),
});
export const CalculateAvailableDatesInRangeRequest = z.object({
  operationId: z
    .string()
    .describe('Operation ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    deliveryAddress: z
      .intersection(
        z.object({
          country: z.string().describe('Country code.').optional().nullable(),
          subdivision: z
            .string()
            .describe(
              'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
            )
            .optional()
            .nullable(),
          city: z.string().describe('City name.').optional().nullable(),
          postalCode: z
            .string()
            .describe('Zip/postal code.')
            .optional()
            .nullable(),
          addressLine2: z
            .string()
            .describe(
              'Free text providing more detailed address info. Usually contains Apt, Suite, and Floor.'
            )
            .optional()
            .nullable(),
          formattedAddress: z
            .string()
            .describe('A string containing the full address of this location.')
            .optional()
            .nullable(),
          hint: z
            .string()
            .describe('Free text to help find the address.')
            .optional()
            .nullable(),
          geocode: z
            .object({
              latitude: z
                .number()
                .describe('Address latitude.')
                .optional()
                .nullable(),
              longitude: z
                .number()
                .describe('Address longitude.')
                .optional()
                .nullable(),
            })
            .describe('Coordinates of the physical address.')
            .optional(),
          countryFullname: z
            .string()
            .describe('Country full name.')
            .optional()
            .nullable(),
          subdivisionFullname: z
            .string()
            .describe('Subdivision full name.')
            .optional()
            .nullable(),
          subdivisions: z
            .array(
              z.object({
                code: z.string().describe('Short subdivision code.').optional(),
                name: z.string().describe('Subdivision full name.').optional(),
              })
            )
            .max(6)
            .optional(),
        }),
        z.xor([
          z.object({
            streetAddress: z.never().optional(),
            addressLine: z.never().optional(),
          }),
          z.object({
            addressLine: z.never().optional(),
            streetAddress: z
              .object({
                number: z.string().describe('Street number.').optional(),
                name: z.string().describe('Street name.').optional(),
                apt: z.string().describe('Apartment number.').optional(),
                formattedAddressLine: z
                  .string()
                  .describe('Optional address line 1')
                  .optional()
                  .nullable(),
              })
              .describe('Street name and number.'),
          }),
          z.object({
            streetAddress: z.never().optional(),
            addressLine: z
              .string()
              .describe(
                'Main address line, usually street and number as free text.'
              ),
          }),
        ])
      )
      .describe(
        'Delivery address.\n\nDelivery fulfillment methods are only considered when calculating date availability if a delivery address is specified.'
      )
      .optional(),
    from: z
      .object({
        day: z
          .number()
          .int()
          .describe('The day of the month.')
          .min(1)
          .max(31)
          .optional(),
        month: z
          .number()
          .int()
          .describe('The month of the year.')
          .min(1)
          .max(12)
          .optional(),
        year: z
          .number()
          .int()
          .describe('The year of the date.')
          .min(2023)
          .max(2200)
          .optional(),
      })
      .describe('Start date and time of the range.'),
    until: z
      .object({
        day: z
          .number()
          .int()
          .describe('The day of the month.')
          .min(1)
          .max(31)
          .optional(),
        month: z
          .number()
          .int()
          .describe('The month of the year.')
          .min(1)
          .max(12)
          .optional(),
        year: z
          .number()
          .int()
          .describe('The year of the date.')
          .min(2023)
          .max(2200)
          .optional(),
      })
      .describe('End date and time of the range.'),
  }),
});
export const CalculateAvailableDatesInRangeResponse = z.object({
  availableDatesPerFulfillmentType: z
    .array(
      z.object({
        fulfilmentType: z
          .enum(['PICKUP', 'DELIVERY'])
          .describe('Fulfillment type.')
          .optional(),
        dates: z
          .array(
            z.object({
              day: z
                .number()
                .int()
                .describe('The day of the month.')
                .min(1)
                .max(31)
                .optional(),
              month: z
                .number()
                .int()
                .describe('The month of the year.')
                .min(1)
                .max(12)
                .optional(),
              year: z
                .number()
                .int()
                .describe('The year of the date.')
                .min(2023)
                .max(2200)
                .optional(),
            })
          )
          .min(1)
          .max(100)
          .optional(),
      })
    )
    .optional(),
});
export const ValidateOperationAddressRequest = z.object({
  operationId: z
    .string()
    .describe('The ID of the operation whose address should be validated.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const ValidateOperationAddressResponse = z.object({
  valid: z.boolean().describe('Whether the address is valid.').optional(),
  violations: z
    .array(
      z.object({
        type: z
          .enum([
            'UNKNOWN',
            'NO_ADDRESS',
            'MISSING_FORMATTED_ADDRESS',
            'INVALID_GEOCODE',
            'MISSING_COUNTRY',
            'MISSING_SUBDIVISION',
          ])
          .describe('Type of violation in the address.')
          .optional(),
      })
    )
    .max(5)
    .optional(),
});
export const BulkUpdateOperationTagsRequest = z.object({
  operationIds: z.array(z.string()).min(1).max(100),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to assign to the operations.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to unassign from the operations.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateOperationTagsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for the updated operation.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the bulk update.')
    .optional(),
});
export const BulkUpdateOperationTagsByFilterRequest = z.object({
  filter: z
    .record(z.string(), z.any())
    .describe('Filter that determines which operations to update tags for.'),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to assign to the operations.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to unassign from the operations.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateOperationTagsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      "Job ID. Pass this ID to Get Async Job ([SDK](https://dev.wix.com/docs/sdk/backend-modules/async-jobs/get-async-job) | [REST](https://dev.wix.com/docs/rest/business-management/async-job/get-async-job)) to track the job's status."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
